"use client";

/**
 * Thin client-side analytics helpers that wrap PostHog capture calls.
 * Ensures event names + payloads stay consistent and easy to audit.
 */
import posthog from "posthog-js";
import type { StepId } from "@/constants/steps";
import type { CoachStage } from "@/lib/accelerators/applicationStage";
import type { ProgramFilterName, ProgramFilterValue } from "@/lib/accelerators/programFilters";
import type {
  PipelineKind,
  PipelineTourStopId,
  ToolbarHintControl,
} from "@/constants/pipelines";
import type { EvaluationTourStopId } from "@/constants/evaluationTour";
import type { EvaluationJourneyState } from "@/lib/evaluation/journeyState";
import type { OnboardingResult } from "@/lib/onboarding/schema";
import type {
  ClaimOutcome,
  CompletionHandoffPath,
  ServerSessionProbe,
} from "@/lib/onboarding/completion";
import type { InlineViewContextReason } from "@/lib/levai/agent/context";
import {
  ANALYTICS_EVENTS,
  ANALYTICS_EVENTS_LABS,
  LEV_COMPLETION_ANALYTICS_EVENTS,
  getCustomLandingPageEventName,
  getLandingPageViewedEventName,
  type AskLevCompletionMode,
  type OrbitProspectScanOutcome,
  type LandingPageCustomEventAction,
  type LandingPageSlug,
} from "./events";
import type { LearnTermMissSurface } from "@/lib/learn/termProposal";
import type { PhoneHandoffSurface } from "@/lib/phoneHandoff/summary";
import type { FileSizeBucket } from "@/lib/utils/file_upload";
import type {
  DeckUploadFailureKind,
  DeckUploadFormat,
  DeckUploadPhase,
  DeckUploadSizeBucket,
  DeckUploadSurface,
} from "@/lib/utils/deckUploadErrors";
import type { InvestorResearchStage } from "@/constants/labs/investorResearch";
import type { MaAdvisorStage } from "@/constants/labs/maAdvisor";
import type { DeckThemeId } from "@/lib/deck/themes";
import type { LiftoffSlideStyle } from "@/components/organisms/deck/DeckSlide";
import type { CraftyGtmMode } from "@/constants/labs/craftyGtm";
import type { StudioJobId } from "@/components/organisms/canvas/FundraisingPanel";
import {
  normalizeTaskAnalyticsActionType,
  normalizeTaskAnalyticsSource,
  type TaskAnalyticsActionType,
  type TaskAnalyticsSource,
  type TaskModel,
} from "./taskSource";

export { readTaskAnalyticsSource, readTaskEventProvenance } from "./taskSource";
import {
  buildYcAppCommonProps,
  getEmailDomain,
  type YcAppCalculationType,
  type YcAppElement,
  type YcAppEntryPoint,
  type YcAppSurface,
} from "./ycPublicApp";
import type { HandoffTarget } from "@/lib/ai/fundraising/deckHandoff";
import type { TourSkipReason, TourStartTrigger } from "@/lib/tutorial/useTour";
import type { IcpEditableDialId } from "@/lib/icp/criteria";
import type { ProgramSlug } from "@/constants/programs";
import type { ThinkBiggerAudience } from "@/constants/thinkBiggerQuotes";
import { normalizeTaskSurface, type TaskSurface } from "@/lib/analytics/taskSurface";

type BaseProps = Record<string, unknown>;

function trackEvent(event: string, properties: BaseProps = {}) {
  if (typeof window === "undefined") return;
  try {
    posthog.capture(event, properties);
  } catch (error) {
    console.warn("[Analytics] Failed to capture event", { event, error });
    /* benign: best-effort PostHog capture */
  }
}

function normalizeTaskActionTypeForEvent(
  rawActionType: string | null | undefined,
): TaskAnalyticsActionType {
  return normalizeTaskAnalyticsActionType(rawActionType);
}

function normalizeTaskSourceForEvent(
  rawSource: string | null | undefined,
  actionType?: string | null,
): TaskAnalyticsSource {
  switch (rawSource) {
    case "ai_generated":
    case "user":
    case "levai_tool":
    case "unknown":
      return rawSource;
    default:
      return normalizeTaskAnalyticsSource(rawSource, actionType);
  }
}

/**
 * Reload click on an App Router error boundary — how often founders hit the
 * crash screen (PR #755 follow-up). Known undercount: when the ROOT layout
 * crashes on initial render, PostHogProvider never ran `posthog.init`, and
 * posthog-js discards pre-init captures — so `context: "root"` misses that
 * subset. The `__loaded` guard keeps posthog's pre-init console.error off the
 * crash screen; a raw-beacon fallback isn't worth the complexity for it.
 */
export function trackErrorBoundaryReloadClicked(props: {
  context: "route" | "root";
  /** The crashed page's pathname — explicit so funnels don't depend on knowing about posthog's auto-attached $pathname. */
  path: string;
}) {
  if (!posthog.__loaded) return;
  trackEvent(ANALYTICS_EVENTS.ERROR_BOUNDARY_RELOAD_CLICKED, {
    context: props.context,
    path: props.path,
  });
}

/**
 * The auto-generation cascade exhausted its attempt budget for a step. The
 * founder sees the chain quietly stop advancing, so this is the only aggregate
 * signal that it happened — step id, attempt count and outcome only, no
 * document content. The hook emits it once per stuck step (reset when the
 * cascade next makes real progress), NOT once per retry cycle.
 */
export function trackAutoGenerationCascadeAbandoned(props: {
  projectId: string;
  /** Typed, matching the server contract in autoGenerationAnalytics.ts — a bare
   *  string would admit invalid values and fragment the PostHog dimension. */
  stepId: StepId;
  /**
   * What the FINAL refusal was. The discriminant that makes this event
   * actionable: it separates "gave up on claim conflicts" (the 2026-08-29
   * retry-storm shape) from "the generation genuinely failed", which the hook
   * otherwise funnels down one code path.
   */
  outcome: "dispatch_rejected" | "failure" | "timeout";
  /** The cap in force when the budget ran out — always AUTO_GENERATION_MAX_DISPATCH_ATTEMPTS, recorded so a future change to that constant is visible in the data. */
  attempts: number;
}) {
  trackEvent(ANALYTICS_EVENTS.BLOCK_AUTO_GENERATION_ABANDONED, {
    projectId: props.projectId,
    stepId: props.stepId,
    outcome: props.outcome,
    attempts: props.attempts,
  });
}

/**
 * The founder clicked "Improve this" on a PROTO block, dispatching the enhance
 * entry point (proto-lifecycle). Emitted at the click boundary, before the
 * dispatch — step id and anonymity only, no block content or PII.
 */
export function trackProtoBlockImproveClicked(props: {
  stepId: StepId;
  isAnonymous: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.PROTO_BLOCK_IMPROVE_CLICKED, {
    stepId: props.stepId,
    isAnonymous: props.isAnonymous,
  });
}

export type ProtoEnhanceSource = "cta" | "improve_all" | "levai" | "first_read";

/**
 * A PROTO block began developing into a DRAFT (proto-blocks enhance). Emitted at
 * the client boundary that starts the enhance — step id, anonymity, whether the
 * founder had edited the proto, and which surface triggered it. No block content.
 */
export function trackProtoBlockEnhanceStarted(props: {
  stepId: StepId;
  isAnonymous: boolean;
  founderEdited: boolean;
  source: ProtoEnhanceSource;
}) {
  trackEvent(ANALYTICS_EVENTS.PROTO_BLOCK_ENHANCE_STARTED, {
    stepId: props.stepId,
    isAnonymous: props.isAnonymous,
    founderEdited: props.founderEdited,
    source: props.source,
  });
}

/** A PROTO block finished developing into a DRAFT (proto-blocks enhance). */
export function trackProtoBlockEnhanceCompleted(props: {
  stepId: StepId;
  isAnonymous: boolean;
  founderEdited: boolean;
  source: ProtoEnhanceSource;
}) {
  trackEvent(ANALYTICS_EVENTS.PROTO_BLOCK_ENHANCE_COMPLETED, {
    stepId: props.stepId,
    isAnonymous: props.isAnonymous,
    founderEdited: props.founderEdited,
    source: props.source,
  });
}

export function trackTaskOpened(props: {
  projectId: string;
  taskId: string;
  openedFrom: "task_list" | "block_pill" | "kanban_board" | "eval_page";
  actionType?: string | null;
  source?: string | null;
  /** `readTaskEventProvenance`; null when the row cannot tell. */
  task_model?: TaskModel | null;
  status?: string | null;
}) {
  // Stay null when the caller has no source signal at all so we don't
  // fabricate "unknown" rows. Evaluation tasks always classify as ai_generated
  // even without details.source — the normalizer handles that fallback.
  const source =
    props.source != null || props.actionType === "evaluation"
      ? normalizeTaskSourceForEvent(props.source, props.actionType)
      : null;
  trackEvent(ANALYTICS_EVENTS.TASK_OPENED, {
    projectId: props.projectId,
    taskId: props.taskId,
    // Idempotent to FIRST open per task: the lightbox opens from 3+ handlers and
    // re-clicks are common, which inflated the dashboard's "Opened" bar. Dedup to
    // one open per task; if we later want re-open frequency, add a distinct event
    // rather than un-dedup this one.
    $insert_id: `${ANALYTICS_EVENTS.TASK_OPENED}:${props.taskId}`,
    openedFrom: props.openedFrom,
    actionType: props.actionType
      ? normalizeTaskActionTypeForEvent(props.actionType)
      : null,
    source,
    task_model: props.task_model ?? null,
    status: props.status ?? null,
  });
  setLifecycleOnce({ first_task_opened_at: new Date().toISOString() });
}

/** `surface` is the surface that OPENED the card: this event is the card view. */
export function trackTaskMarkedRead(props: {
  projectId: string;
  taskId: string;
  surface: TaskSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_MARKED_READ, {
    projectId: props.projectId,
    taskId: props.taskId,
    surface: normalizeTaskSurface(props.surface),
  });
}

export function trackTaskMarkedUnread(props: {
  projectId: string;
  taskId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_MARKED_UNREAD, {
    projectId: props.projectId,
    taskId: props.taskId,
  });
}

export function trackTaskListReordered(props: {
  projectId: string;
  taskCount: number;
  surface: TaskSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_LIST_REORDERED, {
    projectId: props.projectId,
    taskCount: props.taskCount,
    surface: normalizeTaskSurface(props.surface),
  });
}

export function trackTaskRelatedBlockClicked(props: {
  projectId: string;
  taskId: string;
  blockId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_RELATED_BLOCK_CLICKED, {
    projectId: props.projectId,
    taskId: props.taskId,
    blockId: props.blockId,
  });
}

export function trackTaskCreated(props: {
  projectId: string;
  taskId: string;
  source: string | null;
  actionType: string | null;
  weekNumber: number | null;
  stepId: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_CREATED, {
    projectId: props.projectId,
    taskId: props.taskId,
    // One task_created per Action (idempotent), matching the server helper.
    $insert_id: `${ANALYTICS_EVENTS.TASK_CREATED}:${props.taskId}`,
    source: normalizeTaskSourceForEvent(props.source, props.actionType),
    actionType: normalizeTaskActionTypeForEvent(props.actionType),
    weekNumber: props.weekNumber ?? null,
    stepId: props.stepId ?? null,
  });
}

export function trackTaskCompleted(props: {
  projectId: string;
  taskId: string;
  actionType: string | null;
  source: string | null;
  weekNumber: number | null;
  stepId: string | null;
  hadCompletionNotes: boolean;
  completedBy: "user" | "levai";
  surface: TaskSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_COMPLETED, {
    projectId: props.projectId,
    taskId: props.taskId,
    actionType: normalizeTaskActionTypeForEvent(props.actionType),
    source: normalizeTaskSourceForEvent(props.source, props.actionType),
    weekNumber: props.weekNumber ?? null,
    stepId: props.stepId ?? null,
    hadCompletionNotes: props.hadCompletionNotes,
    completedBy: props.completedBy,
    surface: normalizeTaskSurface(props.surface),
  });
  setLifecycleOnce({ first_task_completed_at: new Date().toISOString() });
}

export function trackTaskStatusChanged(props: {
  projectId: string;
  taskId: string;
  oldStatus: string;
  newStatus: string;
  weekNumber: number | null;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_STATUS_CHANGED, {
    projectId: props.projectId,
    taskId: props.taskId,
    oldStatus: props.oldStatus,
    newStatus: props.newStatus,
    weekNumber: props.weekNumber ?? null,
  });
}

/**
 * Mobile-web prototype: a message sent to Lev from a mobile surface. `via`
 * names the control on a surface that has more than one (review: the
 * "Improve it" button or the composer); absent elsewhere. Length only, never
 * the message text.
 */
export function trackMobileLevMessageSent(props: {
  projectId: string;
  surface: "today_cta" | "brain_dump" | "review";
  messageLength: number;
  via?: "button" | "composer";
}) {
  trackEvent(ANALYTICS_EVENTS.MOBILE_LEV_MESSAGE_SENT, props);
}

/** Mobile-web prototype: a phone was redirected from the canvas to the mobile app. */
export function trackMobileAutoRedirected(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.MOBILE_AUTO_REDIRECTED, props);
}

/**
 * Mobile-web prototype: a canvas edit Lev proposed was dismissed rather than
 * saved. IDs and a length only — never the proposed document text.
 */
export function trackMobileProposalDiscarded(props: {
  projectId: string;
  blockId: string;
  contentLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS.MOBILE_PROPOSAL_DISCARDED, props);
}

/** Mobile-web prototype: a "needs you" CTA tapped on the Today digest. */
export function trackMobileImpactCtaTapped(props: {
  projectId: string;
  stepId: string | null;
  ctaLabel: string;
}) {
  trackEvent(ANALYTICS_EVENTS.MOBILE_IMPACT_CTA_TAPPED, props);
}

/** Mobile-web prototype: a founder switched projects via the mobile header control. */
export function trackMobileProjectSwitched(props: { fromProjectId: string; toProjectId: string }) {
  trackEvent(ANALYTICS_EVENTS.MOBILE_PROJECT_SWITCHED, props);
}

/**
 * Mobile-web prototype: a document was approved from the phone.
 *
 * `askedForChanges` records whether the founder ran "Improve it" first, which
 * is the difference between the phone being a rubber stamp and the phone being
 * somewhere real work happens.
 */
export function trackMobileDocApproved(props: {
  projectId: string;
  documentId: string;
  stepId: string | null;
  askedForChanges: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.MOBILE_DOC_APPROVED, props);
}

export function trackTaskDeferred(props: {
  projectId: string;
  taskId: string;
  actionType: string | null;
  source: string | null;
  weekNumber: number | null;
  stepId: string | null;
  surface: TaskSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_DEFERRED, {
    projectId: props.projectId,
    taskId: props.taskId,
    actionType: normalizeTaskActionTypeForEvent(props.actionType),
    source: normalizeTaskSourceForEvent(props.source, props.actionType),
    weekNumber: props.weekNumber ?? null,
    stepId: props.stepId ?? null,
    surface: normalizeTaskSurface(props.surface),
  });
}

// ---------------------------------------------------------------------------
// Task roadmap (gated by task-roadmap) — founder actions on the roadmap-driven
// sidebar. `nodeId` is a fixed roadmap template key, not user content (no PII).
// ---------------------------------------------------------------------------

export function trackRoadmapTaskAddedToList(props: {
  projectId: string;
  nodeId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.ROADMAP_TASK_ADDED_TO_LIST, {
    projectId: props.projectId,
    nodeId: props.nodeId,
  });
}

export function trackRoadmapTaskAssignedToLev(props: { projectId: string; nodeId: string }) {
  trackEvent(ANALYTICS_EVENTS.ROADMAP_TASK_ASSIGNED_TO_LEV, {
    projectId: props.projectId,
    nodeId: props.nodeId,
  });
}

// ---------------------------------------------------------------------------
// Date-scheduling kanban (LEV-849) — strict literal unions per CLAUDE.md
// analytics rule. Re-added when the Upcoming Tasks kanban was restored.
// ---------------------------------------------------------------------------

export type KanbanColumnId =
  | "overdue"
  | "today"
  | "mon"
  | "tue"
  | "wed"
  | "thu"
  | "fri"
  | "sat"
  | "sun"
  | "no_date"
  | "completed";

export function trackTaskScheduledViaDrag(props: {
  projectId: string;
  taskId: string;
  fromColumn: KanbanColumnId;
  toColumn: KanbanColumnId;
  surface: TaskSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_SCHEDULED_VIA_DRAG, {
    projectId: props.projectId,
    taskId: props.taskId,
    fromColumn: props.fromColumn,
    toColumn: props.toColumn,
    surface: normalizeTaskSurface(props.surface),
  });
}

export function trackTaskKanbanOpened(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.TASK_KANBAN_OPENED, {
    projectId: props.projectId,
  });
}

export function trackTaskKanbanCollapsed(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.TASK_KANBAN_COLLAPSED, {
    projectId: props.projectId,
  });
}

export function trackTaskKanbanModeChanged(props: {
  projectId: string;
  mode: "date" | "dimension";
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_KANBAN_MODE_CHANGED, {
    projectId: props.projectId,
    mode: props.mode,
  });
}

/** `weekOffset` is the viewed week relative to the current one (0 = this week, -1 = last week). */
export function trackTaskKanbanWeekNavigated(props: {
  projectId: string;
  direction: "prev" | "next" | "today";
  weekOffset: number;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_KANBAN_WEEK_NAVIGATED, {
    projectId: props.projectId,
    direction: props.direction,
    weekOffset: props.weekOffset,
  });
}

export function trackTaskKanbanMyTasksToggled(props: {
  projectId: string;
  enabled: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_KANBAN_MY_TASKS_TOGGLED, {
    projectId: props.projectId,
    enabled: props.enabled,
  });
}

/**
 * A task the board's column input created (fired on success). `column` is
 * the kind of column, not the day: "today", "future_day" (a later By Date
 * day), or "dimension" (any By Dimension column, Other included).
 */
export function trackTaskKanbanTaskAdded(props: {
  projectId: string;
  mode: "date" | "dimension";
  column: "today" | "future_day" | "dimension";
  surface: TaskSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_KANBAN_TASK_ADDED, {
    projectId: props.projectId,
    mode: props.mode,
    column: props.column,
    surface: normalizeTaskSurface(props.surface),
  });
}

/**
 * LEV-2006: the pipeline board found the founder already at their Apollo
 * prospect cap and opened the "request more" popup without calling the
 * server, so no server-side `prospect_cap_reached` fires for this hit.
 * `checkedBy: "board"` separates these from the server's captures.
 * Counts/ids only.
 */
export function trackProspectCapReached(props: {
  projectId: string;
  path: "manual_add" | "agentic_flow";
  used: number;
  cap: number;
}) {
  trackEvent(ANALYTICS_EVENTS.PROSPECT_CAP_REACHED, { ...props, checkedBy: "board" });
}

export function trackRoadmapBoardOpened(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.ROADMAP_BOARD_OPENED, {
    projectId: props.projectId,
  });
}

export function trackTaskDeleted(props: {
  projectId: string;
  taskId: string;
  actionType: string | null;
  source: string | null;
  weekNumber: number | null;
  stepId: string | null;
  completedBeforeDelete: boolean;
  surface: TaskSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_DELETED, {
    projectId: props.projectId,
    taskId: props.taskId,
    actionType: normalizeTaskActionTypeForEvent(props.actionType),
    source: normalizeTaskSourceForEvent(props.source, props.actionType),
    weekNumber: props.weekNumber ?? null,
    stepId: props.stepId ?? null,
    completedBeforeDelete: props.completedBeforeDelete,
    surface: normalizeTaskSurface(props.surface),
  });
}

export function trackTaskCompletionNotesAdded(props: {
  projectId: string;
  taskId: string;
  notesLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_COMPLETION_NOTES_ADDED, props);
}

export function trackTaskEdited(props: {
  projectId: string;
  taskId: string;
  fieldsEdited: string[];
  editedBy?: "user";
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_EDITED, {
    projectId: props.projectId,
    taskId: props.taskId,
    fieldsEdited: props.fieldsEdited,
    editedBy: props.editedBy ?? "user",
  });
}

export function trackTaskCreateClicked(props: { projectId: string; surface: TaskSurface }) {
  trackEvent(ANALYTICS_EVENTS.TASK_CREATE_CLICKED, {
    projectId: props.projectId,
    surface: normalizeTaskSurface(props.surface),
  });
}

/**
 * LEV-1067: `ask_lev_clicked`. Fired CLIENT-side when the founder clicks the
 * "Ask Lev to complete" CTA, before/independent of the mutation result so the
 * intent is captured even if the mutation later fails. Carries the gated
 * completion mode + ids only — no task text.
 */
export function trackAskLevClicked(props: {
  projectId: string;
  actionId: string;
  completionMode: AskLevCompletionMode;
}) {
  trackEvent(LEV_COMPLETION_ANALYTICS_EVENTS.ASK_LEV_CLICKED, props);
}

/**
 * `lev_completion_proposed`. Fired CLIENT-side from the completion flow when
 * Lev returns a block-update proposal for a Category A ("Ask Lev to complete")
 * task. ids only — never the proposed AI content.
 */
export function trackLevCompletionProposed(props: {
  projectId: string;
  actionId: string;
}) {
  trackEvent(LEV_COMPLETION_ANALYTICS_EVENTS.COMPLETION_PROPOSED, props);
}

/**
 * `lev_completion_accepted`. Fired CLIENT-side when the founder accepts a
 * Lev-driven completion proposal (`pendingCompletion.completedBy === "lev"`).
 * ids only.
 */
export function trackLevCompletionAccepted(props: {
  projectId: string;
  actionId: string;
}) {
  trackEvent(LEV_COMPLETION_ANALYTICS_EVENTS.COMPLETION_ACCEPTED, props);
}

/**
 * `lev_completion_rejected`. Fired CLIENT-side when the founder rejects a
 * Lev-driven completion proposal (`pendingCompletion.completedBy === "lev"`).
 * ids only.
 */
export function trackLevCompletionRejected(props: {
  projectId: string;
  actionId: string;
}) {
  trackEvent(LEV_COMPLETION_ANALYTICS_EVENTS.COMPLETION_REJECTED, props);
}

export function trackTaskWeeklyReportClicked(props: {
  projectId: string;
  weekNumber: number;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_WEEKLY_REPORT_CLICKED, props);
}

/**
 * Fired when a founder follows the low-data weekly-report email's "Update Lev"
 * button and the app arms the founder-update capture on load. Pure UI action
 * at page load → client-side capture (analytics-conventions.md).
 */
export function trackWeeklyReportEmailUpdateLevOpened(props: {
  projectId: string;
  weekNumber: number;
}) {
  trackEvent(ANALYTICS_EVENTS.TASK_WEEKLY_REPORT_EMAIL_UPDATE_LEV_OPENED, props);
}

export function trackTasksGenerateStarted(props: {
  projectId: string;
  requestId: string;
  targetCount: number;
  actionCount?: number;
}) {
  trackEvent(ANALYTICS_EVENTS.TASKS_GENERATE_STARTED, props);
}

export function trackTaskWeeklyReportOpened(props: {
  projectId: string;
  weekNumber: number;
  currentWeekNumber: number;
  isFirstView: boolean;
  completedCount?: number | null;
  totalCount?: number | null;
}) {
  const diff = props.currentWeekNumber - props.weekNumber;
  trackEvent(ANALYTICS_EVENTS.TASK_WEEKLY_REPORT_OPENED, {
    projectId: props.projectId,
    weekNumber: props.weekNumber,
    currentWeekNumber: props.currentWeekNumber,
    weeksBehindCurrent: Math.max(0, diff),
    isLatestWeek: diff === 0,
    isFirstView: props.isFirstView,
    completedCount: props.completedCount ?? null,
    totalCount: props.totalCount ?? null,
  });
  setLifecycleOnce({
    first_task_weekly_report_opened_at: new Date().toISOString(),
  });
}

export function trackDocApproveClicked(props: {
  projectId: string;
  documentId: string;
  stepId?: string | null;
  documentStatus: string;
  hasChangesSinceApproval: boolean;
  requiresEditConfirmation: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.DOC_APPROVE_CLICKED, props);
}

export function trackApproveWithoutEditsConfirmed(props: {
  projectId: string;
  documentId: string;
  stepId?: string | null;
  approvedVersionId?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.DOC_APPROVE_WITHOUT_EDITS_CONFIRMED, props);
}

export function trackDeliverableEnqueuedClient(props: {
  projectId: string;
  parentDocumentId: string;
  deliverableId: string;
  source: "auto" | "manual" | "manual-draft";
}) {
  trackEvent(ANALYTICS_EVENTS.DELIVERABLE_AUTOGEN_ENQUEUED, props);
}

export function trackDomainCheck(props: {
  projectId: string;
  domain: string;
  available: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.DOMAIN_CHECK, props);
}

export function trackLovableClick(props: {
  projectId: string;
  promptLength: number;
  documentIds: string[];
}) {
  trackEvent(ANALYTICS_EVENTS.LOVABLE_CLICKED, props);
}

export function trackDocumentDownloadClicked(props: {
  projectId: string;
  documentId: string;
  documentType: "document" | "deliverable";
  stepId?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.DOC_DOWNLOAD_CLICKED, props);
}

export function trackDocumentEditClicked(props: {
  projectId: string;
  documentId: string;
  documentType: "document" | "deliverable";
  stepId?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.DOC_EDIT_CLICKED, props);
}

/** A founder's manual edit reached the server as a new version. Ids only, never the text. */
export function trackDocumentSaveSucceeded(props: {
  projectId: string;
  documentId: string;
  documentType: "document" | "deliverable";
  stepId?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.DOC_SAVE_SUCCEEDED, props);
}

/** The founder answered "Discard your edits?" over an unsaved lightbox draft. Ids only, never the text. */
export function trackDocumentEditDiscardDecided(props: {
  projectId: string;
  documentId: string;
  decision: "discard" | "keep";
}) {
  trackEvent(ANALYTICS_EVENTS.DOC_EDIT_DISCARD_DECIDED, props);
}

export function trackDocumentExpandClicked(props: {
  projectId: string;
  documentId: string;
  documentType: "document" | "deliverable";
  stepId?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.DOC_EXPAND_CLICKED, props);
}

export function trackTeamLinkedinProfilesClicked(props: {
  projectId: string;
  documentId: string;
  stepId: string;
  savedLinkedinUrlCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.TEAM_LINKEDIN_PROFILES_CLICKED, props);
}

export function trackTeamLinkedinProfilesSubmitted(props: {
  projectId: string;
  documentId?: string | null;
  stepId: string;
  source: "team-create" | "team-refresh";
  linkedinUrlCount: number;
  previousLinkedinUrlCount?: number | null;
  changed?: boolean | null;
}) {
  trackEvent(ANALYTICS_EVENTS.TEAM_LINKEDIN_PROFILES_SUBMITTED, {
    projectId: props.projectId,
    documentId: props.documentId ?? null,
    stepId: props.stepId,
    source: props.source,
    linkedinUrlCount: props.linkedinUrlCount,
    previousLinkedinUrlCount: props.previousLinkedinUrlCount ?? null,
    changed: props.changed ?? null,
  });
}

/** LEV-2358: the founder fit story panel was shown, once per Team open. State only, no story text. */
export function trackTeamFounderFitViewed(props: {
  projectId: string;
  state: "empty" | "stale" | "ready";
}) {
  trackEvent(ANALYTICS_EVENTS.TEAM_FOUNDER_FIT_VIEWED, props);
}

export function trackChatSessionInitiated(props: {
  projectId: string;
  documentId: string;
  documentType: "document" | "deliverable";
  stepId?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_SESSION_INITIATED, props);
}

export function trackChatDocumentUpdated(props: {
  projectId: string;
  documentId: string;
  documentType: "document" | "deliverable";
  stepId?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_DOCUMENT_UPDATED, props);
}

// Onboarding chat tracking functions
export function trackOnboardingChatStarted(props: {
  messageLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_STARTED, props);
}

export function trackOnboardingChatMessageSent(props: {
  messageNumber: number;
  messageLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_MESSAGE_SENT, props);
}

export function trackOnboardingChatSkipClicked(props: {
  messageCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_SKIP_CLICKED, props);
}

/** Counts and flags only — never the discarded conversation's text. */
export function trackOnboardingChatReset(props: {
  messageCount: number;
  hadResult: boolean;
  detachOk: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_RESET, props);
}

/**
 * A materials parse (deck upload — LEV-1295; website URL — LEV-1431) failed at
 * some phase. Anonymous pre-project flow — no projectId. `fileSizeBucket` is
 * deck-only; a site failure carries no size and never the URL or host.
 */
export function trackOnboardingMaterialsParseFailed(props: {
  sourceType: "deck" | "site";
  phase: "blob-upload" | "post" | "poll" | "job";
  httpStatus: number | null;
  fileSizeBucket?: FileSizeBucket;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_MATERIALS_PARSE_FAILED, props);
}

/**
 * A deck upload failed, on any of the four surfaces that take one
 * (LEV-2923). `sizeBucket` is null when the file's size was never known —
 * e.g. a pre-upload format rejection has nothing to bucket. `projectId` is
 * omitted pre-claim in onboarding, where there is no project yet.
 */
export function trackDeckUploadFailed(props: {
  kind: DeckUploadFailureKind;
  surface: DeckUploadSurface;
  format: DeckUploadFormat;
  sizeBucket: DeckUploadSizeBucket | null;
  phase: DeckUploadPhase;
  projectId?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.DECK_UPLOAD_FAILED, {
    kind: props.kind,
    surface: props.surface,
    format: props.format,
    sizeBucket: props.sizeBucket,
    phase: props.phase,
    projectId: props.projectId ?? null,
  });
}

/**
 * `wasSuppressed` (nudged/released only) says whether a streamed "complete"
 * was being held back when the parse gate resolved; false means the parse
 * landed with no CTA waiting and the nudge only delivered the read.
 * `releaseReason` narrows a "released" outcome: "read_delivered" when the
 * gate released because the just-finished turn's own [MATERIALS_READ_DUE]
 * header already covered every landed source (LEV-1969 — the poll GET can
 * report a source still due from a pre-persist snapshot while that turn is
 * already delivering it); omitted for the other release paths (all parses
 * failed, a founder action pre-empted the gate, the message cap was hit, or
 * the Build CTA / Skip ended the turn-taking).
 */
export function trackOnboardingBuildCtaGated(props: {
  outcome: "suppressed" | "nudged" | "released";
  wasSuppressed?: boolean;
  releaseReason?: "read_delivered";
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_BUILD_CTA_GATED, props);
}

/**
 * The founder pressed Enter before Lev had any visible reply — the composer
 * shows "You can answer once Lev starts replying." No properties: this is a
 * pure rate signal, not text.
 */
export function trackOnboardingAnswerBeforeReply() {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_ANSWER_BEFORE_REPLY);
}

export function trackOnboardingChatBuildConfirmed(props: {
  messageCount: number;
  hasIdentity: boolean;
  hasJourney: boolean;
  /** A website URL was staged for parsing this session (boolean only — never the host). */
  hasSiteParse?: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_BUILD_CONFIRMED, props);
}

export function trackOnboardingChatBuildDeclined(props: {
  messageCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_BUILD_DECLINED, props);
}

export function trackOnboardingChatRestored(props: {
  messageCount: number;
  /** Which UI offered the restore: the in-chat "restore?" prompt, or the LEV-1925 sign-in resume flow landing back on the chat. */
  trigger: "prompt" | "sign-in-resume";
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_RESTORED, props);
}

export function trackOnboardingChatError(props: {
  errorMessage: string;
  messageCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_ERROR, props);
}

/** "Try again" clicked on a failed-turn notice. Count only — never the text. */
export function trackOnboardingChatRetryClicked(props: {
  messageCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CHAT_RETRY_CLICKED, props);
}

/**
 * A founder explicitly declined an uncorroborated onboarding claim link at
 * /onboarding/complete (ADR 041 confirm gate). Expected product action, not
 * an error — but the rate doubles as abuse signal (attacker-mailed links).
 * No token or idea data attached.
 */
export function trackOnboardingClaimTokenDeclined() {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_CLAIM_TOKEN_DECLINED, {});
}

/**
 * A signed-up visitor landed on /onboarding/complete with NO idea signal:
 * claim NOT_FOUND (`claimOutcome: "no-session"`), or a successfully claimed
 * session nobody sent a message into (`claimOutcome: "empty"`, per
 * isEmptyClaimedSession). Also no sessionStorage backup, pending stash, or `?oc=`
 * URL token. A stashed mint token is allowed on the `empty` path: the landing
 * input mints one before the first send.
 * This is the organic arrival path — Clerk's global sign-up redirect sends every
 * new account here whether or not they ever entered an idea — an expected funnel
 * event, NOT an error (2026-08-14 false-positive fix; Sentry issue 7668631999).
 */
export function trackOnboardingCompletionNoIdeaSource(props: {
  claimOutcome: ClaimOutcome;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETE_NO_IDEA_SOURCE, props);
}

/**
 * An idea signal existed (handoff stash, backup, or a server session that was
 * expired/claimed/unreachable; a claim token only when isEmptyClaimedSession
 * is false) but conversion could not use any source — the founder's idea is
 * genuinely at risk. Mirrors the blocking Sentry capture so funnel dashboards
 * and error triage see the same buckets. Flags only — no idea text (analytics-conventions.md).
 */
export function trackOnboardingCompletionIdeaLost(props: {
  handoffPath: CompletionHandoffPath;
  claimOutcome: ClaimOutcome;
  hadClaim: boolean;
  hadBackup: boolean;
  hadPendingIdea: boolean;
  hadUrlToken: boolean;
  hadStashedToken: boolean;
  tokenWasConfirmed: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETE_IDEA_LOST, props);
}

/**
 * A signed-in founder landed on /onboarding/complete for a session that already
 * produced a project (stale redirect, bookmark, or an anonymous project claimed
 * at signup). They are routed to the existing project and no second project is
 * created. Id only — no idea text (analytics-conventions.md).
 */
/**
 * `opened`: routed to the live project. `deleted`: the converted project was
 * soft-deleted, so the founder was sent home (no projectId to report).
 */
export function trackOnboardingCompletionAlreadyConverted(props: {
  outcome: "opened" | "deleted" | "archived";
  projectId?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETE_ALREADY_CONVERTED, props);
}

/**
 * A signed-in founder with an unsent chat message was routed back to resume
 * it rather than claimed into "We lost your idea" (LEV-1925). No idea text —
 * `userMessageCount` and `serverProbe` are the only signal PostHog needs to
 * size how often the resume path fires versus the ordinary claim.
 */
export function trackOnboardingCompletionChatResumed(props: {
  userMessageCount: number;
  serverProbe: ServerSessionProbe;
  hadStashedToken: boolean;
  returnTo: "home" | "partner";
}) {
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETE_CHAT_RESUMED, props);
}

/**
 * The single writer of onboarding_* person properties; add new ones here.
 * Never throws, and tolerates a partially-filled result: the landing-chat
 * handoff calls it before sign-up, where nested sections can be missing.
 */
export function setOnboardingPersonProperties(result: OnboardingResult): void {
  try {
    posthog.setPersonProperties({
      onboarding_rubric_type: result.rubricType,
      onboarding_status: result.status,
      // Identity
      onboarding_founder_name: result.identity?.name ?? undefined,
      onboarding_founder_title: result.identity?.title ?? undefined,
      onboarding_founder_linkedin: result.identity?.linkedinUrl ?? undefined,
      onboarding_founder_background: result.identity?.background ?? undefined,
      // Journey
      onboarding_journey_stage: result.journey?.stage,
      onboarding_journey_narrative: result.journey?.narrative ?? undefined,
      onboarding_journey_milestone_count: result.journey?.milestones?.length ?? 0,
      // Big idea
      onboarding_idea_one_liner: result.bigIdea?.oneLiner ?? undefined,
      onboarding_idea_problem: result.bigIdea?.problem ?? undefined,
      onboarding_idea_solution: result.bigIdea?.solution ?? undefined,
      onboarding_idea_target_audience: result.bigIdea?.targetAudience ?? undefined,
      onboarding_idea_refined: result.bigIdea?.refinedIdea ?? undefined,
    });
  } catch {
    /* benign: PostHog not initialized */
  }
}

export function trackOnboardingResultCaptured(props: {
  projectId: string;
  result: OnboardingResult;
  /** Which record built the project: the server claim, the sessionStorage fallback, or the pending-idea rescue. */
  source?: "claim" | "backup" | "pending";
  /** Which surface handed the idea off (idea-finder | zeitgeist | landing-chat | unknown). */
  handoffPath?: CompletionHandoffPath;
}) {
  const { result } = props;

  // Event properties: boolean flags for quick PostHog filters
  trackEvent(ANALYTICS_EVENTS.ONBOARDING_RESULT_CAPTURED, {
    projectId: props.projectId,
    conversionSource: props.source,
    handoffPath: props.handoffPath,
    rubricType: result.rubricType,
    journeyStage: result.journey.stage,
    onboardingStatus: result.status,
    hasIdentity: !!result.identity.name,
    hasLinkedin: !!result.identity.linkedinUrl,
    hasBackground: !!result.identity.background,
    hasSiteSignals: !!(result.siteSignals?.inferredName || result.siteSignals?.tagline || result.siteSignals?.icpHint || result.siteSignals?.customerLogos?.length),
    hasProblem: !!result.bigIdea.problem,
    hasSolution: !!result.bigIdea.solution,
    hasTargetAudience: !!result.bigIdea.targetAudience,
    milestoneCount: result.journey.milestones?.length ?? 0,
  });

  // Person properties: actual values for segmentation and user profiles
  // Note: email is already set via posthog.identify() in ClientProvider
  setOnboardingPersonProperties(result);
}

// Landing page navigation tracking
export function trackLandingNavClicked(props: {
  section: string;
}) {
  trackEvent(ANALYTICS_EVENTS.LANDING_NAV_CLICKED, props);
}

/** `stageIndex` is null for the closing "Where Lev fits" CTA. */
export function trackLearnGuideCtaClicked(props: {
  guideSlug: string;
  placement: string;
  stageIndex: number | null;
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_GUIDE_CTA_CLICKED, props);
}

export function trackLandingLoginClicked() {
  trackEvent(ANALYTICS_EVENTS.LANDING_LOGIN_CLICKED);
}

export function trackLandingSignupClicked(props: {
  location: "nav" | "mobile_nav";
}) {
  trackEvent(ANALYTICS_EVENTS.LANDING_SIGNUP_CLICKED, props);
}

export function trackLandingCtaClicked(props: {
  section: string;
  cardIndex?: number;
}) {
  trackEvent(ANALYTICS_EVENTS.LANDING_CTA_CLICKED, props);
}

export function trackLandingCapabilityCardClicked(props: {
  card: "validate_idea" | "early_gtm" | "fundraising";
}) {
  trackEvent(ANALYTICS_EVENTS.LANDING_CAPABILITY_CARD_CLICKED, props);
}

export function trackLandingTrainingTopicClicked(props: {
  topic:
    | "customer_validation"
    | "market_sizing"
    | "pricing"
    | "branding"
    | "go_to_market"
    | "fundraising";
}) {
  trackEvent(ANALYTICS_EVENTS.LANDING_TRAINING_TOPIC_CLICKED, props);
}

export function trackLandingFaqToggled(props: {
  question: string;
  isOpen: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.LANDING_FAQ_TOGGLED, props);
}

export function trackLandingFooterLinkClicked(props: {
  linkName: string;
  href: string;
}) {
  trackEvent(ANALYTICS_EVENTS.LANDING_FOOTER_LINK_CLICKED, props);
}

// LEV-2359 program funnel. Closed-set properties only: never founder text,
// file names or URLs.
export function trackProgramLandingViewed(props: {
  program: ProgramSlug;
  referralSource?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
}) {
  const properties: BaseProps = { program: props.program };
  for (const key of ["referralSource", "utmSource", "utmMedium", "utmCampaign"] as const) {
    if (props[key]) properties[key] = props[key];
  }
  trackEvent(ANALYTICS_EVENTS.PROGRAM_LANDING_VIEWED, properties);
}

/** The landing actions fire before any project exists; keep_working_in_lev fires inside one, so it carries projectId. */
export function trackProgramCtaClicked(
  props:
    | { program: ProgramSlug; action: "upload_deck" }
    | { program: ProgramSlug; action: "keep_working_in_lev"; projectId: string },
) {
  trackEvent(ANALYTICS_EVENTS.PROGRAM_CTA_CLICKED, props);
}

export function trackProgramMaterialsSubmitted(props: {
  program: ProgramSlug;
  kind: "deck" | "document" | "website";
}) {
  trackEvent(ANALYTICS_EVENTS.PROGRAM_MATERIALS_SUBMITTED, props);
}

export function trackProgramFirstReadShown(props: {
  program: ProgramSlug;
  readKind: "materials_read";
}) {
  trackEvent(ANALYTICS_EVENTS.PROGRAM_FIRST_READ_SHOWN, props);
}

// Accelerators canvas row (accelerators-row). Closed-set properties only.
export function trackAcceleratorLightboxOpened(props: {
  projectId: string;
  view: "programs" | "application";
  formatId: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_LIGHTBOX_OPENED, props);
}

export function trackAcceleratorApplicationStageSelected(props: {
  projectId: string;
  formatId: string;
  stage: string;
  via: "steps";
}) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_APPLICATION_STAGE_SELECTED, props);
}

export function trackAcceleratorProgramChipClicked(props: {
  projectId: string;
  formatId: string;
  confirmed: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_PROGRAM_CHIP_CLICKED, props);
}

export function trackAcceleratorCanvasIntroPosted(props: { projectId: string; formatId: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_CANVAS_INTRO_POSTED, props);
}

export function trackAcceleratorProgramsNudgeShown(props: {
  projectId: string;
  surface: "banner" | "chat";
}) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_PROGRAMS_NUDGE_SHOWN, props);
}

export function trackAcceleratorProgramsNudgeClicked(props: {
  projectId: string;
  surface: "banner" | "chip";
  view: "programs" | "application";
  formatId: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_PROGRAMS_NUDGE_CLICKED, props);
}

export function trackAcceleratorProgramsNudgeDismissed(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_PROGRAMS_NUDGE_DISMISSED, props);
}

export function trackAcceleratorAnnouncementShown(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_ANNOUNCEMENT_SHOWN, props);
}

export function trackAcceleratorAnnouncementClicked(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_ANNOUNCEMENT_CLICKED, props);
}

export function trackAcceleratorAnnouncementDismissed(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_ANNOUNCEMENT_DISMISSED, props);
}

export function trackAcceleratorFirstStopReached(props: { projectId: string; program: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_FIRST_STOP_REACHED, props);
}

export function trackAcceleratorFirstStopAutoPicked(props: { projectId: string; program: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_FIRST_STOP_AUTO_PICKED, props);
}

/** `from`/`to` are lightboxContentKey-style ids ("programs" or "application:<formatId>"). */
export function trackAcceleratorBlockNavUsed(props: { projectId: string; from: string; to: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_BLOCK_NAV_USED, props);
}

export function trackAcceleratorProgramApplyLinkClicked(props: {
  projectId: string;
  programKey: string;
}) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_PROGRAM_APPLY_LINK_CLICKED, props);
}

export function trackAcceleratorProgramSuggestionsRefreshed(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_PROGRAM_SUGGESTIONS_REFRESHED, props);
}

/**
 * The search box (LEV-2640) is its own branch, not routed through the fixed
 * `value` enum: `hasQuery` and `queryLength` carry what changed without ever
 * carrying the typed text, which this event's fixed-enum `value` shape
 * cannot hold anyway.
 */
export function trackAcceleratorProgramsFilterChanged(
  props:
    | { projectId: string; filter: ProgramFilterName; value: ProgramFilterValue }
    | { projectId: string; filter: "search"; hasQuery: boolean; queryLength: number },
) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_PROGRAMS_FILTER_CHANGED, props);
}

/** "Clear filters" on the All programs list: one event, not one per filter reset. */
export function trackAcceleratorProgramsFiltersCleared(props: { projectId: string; clearedCount: number }) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_PROGRAMS_FILTERS_CLEARED, props);
}

export type ThinkBiggerSurface = "deck" | "raise-summary";

export function trackFundraisingThinkBiggerLevelChanged(props: {
  projectId: string;
  level: number;
  surface: ThinkBiggerSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.FUNDRAISING_THINK_BIGGER_LEVEL_CHANGED, props);
}

export type ThinkBiggerQuoteSurface = ThinkBiggerSurface | "application";

export function trackThinkBiggerQuotePaged(props: {
  projectId: string;
  surface: ThinkBiggerQuoteSurface;
  audience: ThinkBiggerAudience;
  index: number;
  direction: "previous" | "next";
}) {
  trackEvent(ANALYTICS_EVENTS.THINK_BIGGER_QUOTE_PAGED, props);
}

export function trackPhoneHandoffShown(props: {
  projectId: string;
  surface: PhoneHandoffSurface;
  focusesApplication: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.PHONE_HANDOFF_SHOWN, props);
}

export function trackPhoneHandoffLinkCopied(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.PHONE_HANDOFF_LINK_COPIED, props);
}

export function trackAcceleratorDraftWithoutWaitingClicked(props: {
  projectId: string;
  formatId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.ACCELERATOR_DRAFT_WITHOUT_WAITING_CLICKED, props);
}

export function trackProgramBuildClicked(props: { program: ProgramSlug }) {
  trackEvent(ANALYTICS_EVENTS.PROGRAM_BUILD_CLICKED, props);
}

export function trackProgramHandoffViewed(props: {
  program: ProgramSlug;
  projectId: string;
  hadDeck: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.PROGRAM_HANDOFF_VIEWED, props);
}

export function trackProgramHandoffCompleted(props: {
  program: ProgramSlug;
  projectId: string;
  bigIdea: "built" | "skipped" | "failed" | "timed_out";
  destination: "application" | "canvas";
  durationMs: number;
}) {
  trackEvent(ANALYTICS_EVENTS.PROGRAM_HANDOFF_COMPLETED, props);
}

// Founder apps tracking
export function trackFounderAppClicked(props: {
  appName: string;
  location: "card" | "footer";
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_APP_CLICKED, props);
}

/**
 * A visitor applied the Venture Graph directory's search or filters. Carries
 * which controls they used and nothing they typed: `hasQuery` is a boolean, and
 * the selects report only whether one was chosen (plus the fund-timing value,
 * which is one of three fixed options).
 */
export function trackVentureGraphFilterApplied(props: {
  hasQuery: boolean;
  hasSector: boolean;
  hasLocation: boolean;
  fund: "raising" | "new" | "mid" | "none";
}) {
  trackEvent(ANALYTICS_EVENTS.VENTURE_GRAPH_FILTER_APPLIED, props);
}

/**
 * A reviewer decided a Suggested-add card: one-click Add/Decline, or Add
 * after editing the pre-filled form. No firm name, card id or filing text —
 * action/path/confidence/ruleVersion is all that leaves the browser.
 */
export function trackVentureGraphSuggestionDecided(props: {
  action: "add" | "decline";
  path: "oneClick" | "edited";
  confidence: number;
  ruleVersion: number;
}) {
  trackEvent(ANALYTICS_EVENTS.VENTURE_GRAPH_SUGGESTION_DECIDED, props);
}

export function trackLandingPageViewed(slug: LandingPageSlug) {
  trackEvent(getLandingPageViewedEventName(slug));
}

export function trackLandingPageCustomEvent<
  TAction extends LandingPageCustomEventAction,
>(
  slug: LandingPageSlug,
  action: TAction,
  properties: BaseProps = {},
) {
  trackEvent(getCustomLandingPageEventName(slug, action), properties);
}

// Fires only from YcLandingHero: the legacy /yc page, which renders while the
// YC program lander is not live (accelerators-straight-in OFF or YC not
// launched).
export function trackYcLandingPageViewed() {
  trackEvent(ANALYTICS_EVENTS.YC_LANDING_PAGE_VIEWED);
}

export function trackYcLandingBuildClicked(props: {
  isLoggedIn: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.YC_LANDING_BUILD_CLICKED, props);
}

export function trackYcLandingScoreClicked(props: {
  isLoggedIn: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.YC_LANDING_SCORE_CLICKED, props);
}

export function trackYcLandingGenerateClicked(props: {
  isLoggedIn: boolean;
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.YC_LANDING_GENERATE_CLICKED, props);
}

type YcAppCommonTrackProps = {
  blockMode?: "not-started" | "generated" | "completed";
  isLoggedIn?: boolean;
  percentComplete?: number;
  projectId?: string | null;
  userId?: string | null;
  ycAppElement?: YcAppElement;
  ycAppEntryPoint?: YcAppEntryPoint;
  ycAppSurface?: YcAppSurface;
};

function buildYcAppClientProps(props: YcAppCommonTrackProps) {
  return {
    ...buildYcAppCommonProps({
      isLoggedIn: props.isLoggedIn,
      projectId: props.projectId,
      userId: props.userId,
      ycAppElement: props.ycAppElement,
      ycAppEntryPoint: props.ycAppEntryPoint,
      ycAppSurface: props.ycAppSurface,
    }),
    blockMode: props.blockMode ?? null,
    percentComplete: props.percentComplete ?? null,
  };
}

export function trackYcAppViewed(props: YcAppCommonTrackProps) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_VIEWED, buildYcAppClientProps(props));
}

export function trackYcAppStarted(props: YcAppCommonTrackProps & {
  appTextLength?: number;
  companyNameProvided?: boolean;
  founderCount?: number;
  linkedinUrlCount?: number;
  source?: "lander_cta" | "direct" | "invite_url";
}) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_STARTED, {
    ...buildYcAppClientProps(props),
    appTextLength: props.appTextLength ?? null,
    companyNameProvided: props.companyNameProvided ?? null,
    founderCount: props.founderCount ?? null,
    linkedinUrlCount: props.linkedinUrlCount ?? null,
    source: props.source ?? null,
  });
}

export function trackYcAppEditClicked(props: YcAppCommonTrackProps) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_EDIT_CLICKED, buildYcAppClientProps(props));
}

export function trackYcAppSaveClicked(props: YcAppCommonTrackProps & {
  contentLength?: number;
}) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_SAVE_CLICKED, {
    ...buildYcAppClientProps(props),
    contentLength: props.contentLength ?? null,
  });
}

export function trackYcAppCopyClicked(props: YcAppCommonTrackProps & {
  contentLength?: number;
}) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_COPY_CLICKED, {
    ...buildYcAppClientProps(props),
    contentLength: props.contentLength ?? null,
  });
}

export function trackYcAppScoreCalculated(props: YcAppCommonTrackProps & {
  calculationType: YcAppCalculationType;
  durationMs?: number;
  overallScore: number;
  suggestionsCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_SCORE_CALCULATED, {
    ...buildYcAppClientProps(props),
    calculationType: props.calculationType,
    durationMs: props.durationMs ?? null,
    overallScore: props.overallScore,
    suggestionsCount: props.suggestionsCount,
  });
}

export function trackYcAppSuggestionAnswered(props: YcAppCommonTrackProps & {
  answeredSuggestionCount?: number;
  suggestionId?: string;
  suggestionSection?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_SUGGESTION_ANSWERED, {
    ...buildYcAppClientProps({
      ...props,
      ycAppElement: props.ycAppElement ?? "suggestion",
    }),
    answeredSuggestionCount: props.answeredSuggestionCount ?? null,
    suggestionId: props.suggestionId ?? null,
    suggestionSection: props.suggestionSection ?? "unknown",
  });
}

export function trackYcAppOneLinerSelected(props: YcAppCommonTrackProps & {
  oneLinerId?: string;
  oneLinerLength?: number;
  oneLinerStyle?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_ONE_LINER_SELECTED, {
    ...buildYcAppClientProps({
      ...props,
      ycAppElement: props.ycAppElement ?? "one_liner",
    }),
    oneLinerId: props.oneLinerId ?? null,
    oneLinerLength: props.oneLinerLength ?? null,
    oneLinerStyle: props.oneLinerStyle ?? null,
  });
}

export function trackYcAppDismissed(props: YcAppCommonTrackProps) {
  trackEvent(ANALYTICS_EVENTS.YC_APP_DISMISSED, buildYcAppClientProps(props));
}

export function trackYcAppEmailCaptured(props: {
  email: string;
}) {
  identifyLandingUser(props.email, "first_landing_yc_app_email_at");
  trackEvent(ANALYTICS_EVENTS.YC_APP_EMAIL_CAPTURED, {
    ...buildYcAppCommonProps({ ycAppSurface: "public" }),
    emailDomain: getEmailDomain(props.email),
  });
}

export function trackYcAppSignupClicked() {
  trackEvent(ANALYTICS_EVENTS.YC_APP_SIGNUP_CLICKED, {
    ...buildYcAppCommonProps({ ycAppSurface: "public" }),
  });
}

// Idea Finder tracking (landing page system)
export function trackLandingIdeaFinderFormSubmitted(props: {
  hasName: boolean;
  hasEmail: boolean;
  fieldCount: number;
}) {
  trackLandingPageCustomEvent("idea-finder", "form_submitted", props);
}

export function trackLandingIdeaFinderResultsViewed(props: {
  resultCount: number;
}) {
  trackLandingPageCustomEvent("idea-finder", "results_viewed", props);
}

export function trackLandingIdeaFinderCardExpanded(props: {
  ideaId: string;
  ideaTitle: string;
}) {
  trackLandingPageCustomEvent("idea-finder", "card_expanded", props);
}

export function trackLandingIdeaFinderCardCopied(props: {
  ideaId: string;
  ideaTitle: string;
}) {
  trackLandingPageCustomEvent("idea-finder", "card_copied", props);
}

export function trackLandingIdeaFinderCardDownloaded(props: {
  ideaId: string;
  ideaTitle: string;
}) {
  trackLandingPageCustomEvent("idea-finder", "card_downloaded", props);
}

export function trackLandingIdeaFinderBuildClicked(props: {
  ideaId: string;
  ideaTitle: string;
}) {
  trackLandingPageCustomEvent("idea-finder", "build_clicked", props);
}

export function trackLandingIdeaFinderRerollClicked(props: {
  rerollCount: number;
}) {
  trackLandingPageCustomEvent("idea-finder", "reroll_clicked", props);
}

export function trackLandingIdeaFinderSignupStarted(props: {
  ideaId: string;
  ideaType?: string | null;
}) {
  trackLandingPageCustomEvent("idea-finder", "signup_started", props);
}

/**
 * Identifies a landing-page visitor by normalized email so ClerkIdentify
 * can merge them into the Clerk user ID person after signup.
 */
export function identifyLandingUser(
  email: string,
  lifecycleKey: string,
  properties?: Record<string, unknown>,
) {
  if (typeof window === "undefined") return;
  const normalizedEmail = email.trim().toLowerCase();
  try {
    posthog.identify(normalizedEmail, properties);
    if (properties) {
      posthog.setPersonProperties(properties);
    }
    setLifecycleOnce({ [lifecycleKey]: new Date().toISOString() });
  } catch {
    /* benign: PostHog not initialized */
  }
}

// Founder Type tracking
export function trackFounderTypePageViewed() {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_PAGE_VIEWED);
}

export function trackFounderTypeTestStarted() {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_TEST_STARTED);
}

export function trackFounderTypeQuestionAnswered(props: {
  questionIndex: number;
  totalQuestions: number;
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_QUESTION_ANSWERED, props);
}

export function trackFounderTypeTestCompleted(props: {
  personalityType: string;
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_TEST_COMPLETED, props);
}

export function trackFounderTypeResultViewed(props: {
  personalityType: string;
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_RESULT_VIEWED, props);
}

export function trackFounderTypeCardCopied(props: {
  personalityType: string;
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_CARD_COPIED, props);
}

export function trackFounderTypeCardDownloaded(props: {
  personalityType: string;
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_CARD_DOWNLOADED, props);
}

export function trackFounderTypeLinkedinShared(props: {
  personalityType: string;
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_LINKEDIN_SHARED, props);
}

export function trackFounderTypeBuildClicked(props: {
  personalityType: string;
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_BUILD_CLICKED, props);
}

export function trackFounderTypeCardFlipped(props: {
  personalityType: string;
  isFlipped: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.FOUNDER_TYPE_CARD_FLIPPED, props);
}

// ---------------------------------------------------------------------------
// LevAI chat interaction tracking
// ---------------------------------------------------------------------------

export function trackLevaiChatMessageSent(props: {
  projectId: string;
  messageLength: number;
  blockContext?: string | null;
  inputMethod: "text" | "voice";
  messageCount: number;
  inlineViewContextBytes: number;
  inlineViewContextReason: InlineViewContextReason;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_MESSAGE_SENT, props);
  setLifecycleOnce({ first_levai_message_at: new Date().toISOString() });
}

export function trackLevaiChatMessageReceived(props: {
  projectId: string;
  messageLength: number;
  blockContext?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_MESSAGE_RECEIVED, props);
}

export function trackLevaiChatChipSelected(props: {
  projectId: string;
  chipLabel: string;
  chipPrompt: string;
  blockContext?: string | null;
  /** Always "static" since the model-generated ("dynamic") chip channel was removed 2026-09-03. */
  chipSource: "static";
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_CHIP_SELECTED, props);
}

export function trackLevaiChatEditAccepted(props: {
  projectId: string;
  blockId: string;
  stepId?: string | null;
  contentLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_EDIT_ACCEPTED, props);
}

export function trackLevaiChatEditRejected(props: {
  projectId: string;
  blockId: string;
  stepId?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_EDIT_REJECTED, props);
}

export function trackLevaiChatHistoryCleared(props: {
  projectId: string;
  messageCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_HISTORY_CLEARED, props);
}

export function trackLevaiChatPanelToggled(props: {
  projectId: string;
  isExpanded: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_PANEL_TOGGLED, props);
}

export function trackLevaiChatStreamError(props: {
  projectId: string;
  errorMessage: string;
  retryCount?: number;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_STREAM_ERROR, props);
}

export function trackLevaiChatSessionSummary(props: {
  projectId: string;
  sessionDurationMs: number;
  messagesSent: number;
  messagesReceived: number;
  toolCallsTotal: number;
  editsProposed: number;
  editsAccepted: number;
  editsRejected: number;
  chipsUsed: number;
  avgResponseTimeMs?: number | null;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_SESSION_SUMMARY, props);
}

export function trackLevaiChatAbandonment(props: {
  projectId: string;
  waitedMs: number;
  blockContext?: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_CHAT_ABANDONMENT, props);
}

// ---------------------------------------------------------------------------
// LevAI performance metrics
// ---------------------------------------------------------------------------

export function trackLevaiPerfResponseTime(props: {
  projectId: string;
  durationMs: number;
  firstTokenMs?: number | null;
  streamingDurationMs?: number | null;
  userMessageLength: number;
  responseLength: number;
  toolCallCount: number;
  toolNames: string[];
  blockContext?: string | null;
  toolRoundCount: number;
  sdkRetryCount?: number;
  clientRetryCount?: number;
  reasoningEffort?: string;
  model?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_PERF_RESPONSE_TIME, props);
}

export function trackLevaiPerfTimeout(props: {
  projectId: string;
  phase: "first-byte" | "streaming" | "total";
  elapsedMs: number;
  hadAssistantOutput: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.LEVAI_PERF_TIMEOUT, props);
}

// ---------------------------------------------------------------------------
// Project group + person lifecycle helpers (CDP)
// ---------------------------------------------------------------------------

/**
 * Binds subsequent events to a `project` group and updates its properties.
 * Call on canvas mount and whenever project metadata changes.
 */
export function identifyProjectGroup(projectId: string, properties?: {
  rubric_type?: string | null;
  created_at?: string | null;
  created_from?: string | null;
  is_shared?: boolean;
  owner_id?: string | null;
  latest_step_id?: string | null;
}) {
  if (typeof window === "undefined" || !projectId) return;
  try {
    posthog.group("project", projectId, properties ?? {});
    posthog.register({ lev_project_id: projectId });
  } catch (error) {
    console.warn("[Analytics] Failed to identify project group", { projectId, error });
    /* benign: best-effort PostHog group identification */
  }
}

/** Clears any bound PostHog groups and super properties (e.g. when leaving the canvas). */
export function resetProjectGroup() {
  if (typeof window === "undefined") return;
  try {
    posthog.unregister("lev_project_id");
    posthog.resetGroups();
  } catch {
    /* benign: PostHog not initialized in some environments */
  }
}

/**
 * Sets first-touch lifecycle markers (signup, first project, first approval, ...).
 * Uses PostHog's $set_once semantics so the original timestamp sticks.
 */
export function setLifecycleOnce(properties: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    posthog.setPersonPropertiesForFlags?.(properties);
    posthog.capture("$set", { $set_once: properties });
  } catch {
    /* benign: PostHog not initialized */
  }
}

// ---------------------------------------------------------------------------
// Collaboration & sharing (LEV-553)
// ---------------------------------------------------------------------------

export function trackProjectShared(props: {
  projectId: string;
  action: "enabled" | "disabled";
}) {
  trackEvent(ANALYTICS_EVENTS.PROJECT_SHARED, props);
}

export function trackProjectInviteSent(props: {
  projectId: string;
  method: "email_client" | "gmail_web" | "copy_link";
  recipientCount?: number;
}) {
  trackEvent(ANALYTICS_EVENTS.PROJECT_INVITE_SENT, props);
}

export function trackShareEmailDraftGenerated(props: {
  projectId: string;
  durationMs: number;
  emailLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_EMAIL_DRAFT_GENERATED, props);
}

export function trackShareEmailDraftOpened(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.SHARE_EMAIL_DRAFT_OPENED, props);
}

export function trackShareEmailCopied(props: {
  projectId: string;
  part?: "subject" | "body" | "all";
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_EMAIL_COPIED, props);
}

export function trackShareCommentAdded(props: {
  projectId: string;
  commentId: string;
  commentLength: number;
  hasEmail: boolean;
  isReply: boolean;
  parentCommentId?: string | null;
}) {
  trackEvent(
    props.isReply
      ? ANALYTICS_EVENTS.SHARE_COMMENT_REPLY_ADDED
      : ANALYTICS_EVENTS.SHARE_COMMENT_ADDED,
    {
      projectId: props.projectId,
      commentId: props.commentId,
      commentLength: props.commentLength,
      hasEmail: props.hasEmail,
      parentCommentId: props.parentCommentId ?? null,
    },
  );
}

export function trackShareCommentThreadViewed(props: {
  projectId: string;
  commentId: string;
  replyCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_COMMENT_THREAD_VIEWED, props);
}

export function trackShareCommentHidden(props: {
  projectId: string;
  commentId: string;
  isRoot: boolean;
  hidden: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_COMMENT_HIDDEN, props);
}

export function trackShareCommentPinMoved(props: {
  projectId: string;
  commentId: string;
  movedBy: "viewer" | "owner";
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_COMMENT_PIN_MOVED, props);
}

export function trackSharePageViewed(props: {
  projectId?: string | null;
  isAuthenticated: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_PAGE_VIEWED, props);
}

export function trackWeeklyReportShared(props: {
  projectId: string;
  weekNumber: number;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_WEEKLY_REPORT_SHARED, props);
}

export function trackReportsCollectionShared(props: {
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_REPORTS_COLLECTION_SHARED, props);
}

export function trackReportsCollectionLinkCopied(props: {
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_REPORTS_COLLECTION_LINK_COPIED, props);
}

export function trackReportsCollectionDisabled(props: {
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_REPORTS_COLLECTION_DISABLED, props);
}

export function trackInvestorUpdateShared(props: {
  projectId: string;
  investorUpdateId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_INVESTOR_UPDATE_SHARED, props);
}

export function trackInvestorUpdatesCollectionShared(props: {
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_INVESTOR_UPDATES_COLLECTION_SHARED, props);
}

export function trackInvestorUpdatesCollectionLinkCopied(props: {
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_INVESTOR_UPDATES_COLLECTION_LINK_COPIED, props);
}

export function trackInvestorUpdatesCollectionDisabled(props: {
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_INVESTOR_UPDATES_COLLECTION_DISABLED, props);
}

export function trackBlockShared(props: {
  projectId: string;
  blockId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_BLOCK_SHARED, props);
}

export function trackBlockDisabled(props: {
  projectId: string;
  blockId: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SHARE_BLOCK_DISABLED, props);
}

export function trackBlockCommentCreated(props: {
  projectId: string;
  blockId: string | null;
  isReply: boolean;
  hasTextAnchor: boolean;
  mentionCount: number;
  authorType: "USER" | "AGENT" | "ANONYMOUS";
}) {
  trackEvent(
    props.isReply
      ? ANALYTICS_EVENTS.BLOCK_COMMENT_REPLIED
      : ANALYTICS_EVENTS.BLOCK_COMMENT_CREATED,
    props,
  );
  if (props.mentionCount > 0) {
    trackEvent(ANALYTICS_EVENTS.BLOCK_COMMENT_MENTION_SENT, {
      projectId: props.projectId,
      blockId: props.blockId,
      mentionCount: props.mentionCount,
      authorType: props.authorType,
    });
  }
}

export function trackCollaboratorInvited(props: {
  projectId: string;
  role: "EDITOR" | "COMMENTER";
  inviteeWasExistingUser: boolean;
  /** True when a Clerk Invitation API email was sent (LEV-643). False for
   *  immediate-collaborator paths or race conversions where no email goes
   *  out. */
  clerkInvitationSent?: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.COLLABORATOR_INVITED, props);
}

export function trackCollaboratorRemoved(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.COLLABORATOR_REMOVED, props);
}

export function trackProjectRenamed(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.PROJECT_RENAMED, props);
}

export function trackProjectDeleted(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.PROJECT_DELETED, props);
}

/**
 * The delete confirmation dialog opened (LEV-2015) — the denominator for
 * `project_archived` filtered to `source: "delete_dialog"`, so the steer's
 * conversion rate is measurable. Fired once per open, regardless of which
 * card menu (active/shared vs. archived) triggered it.
 */
export function trackProjectDeleteDialogOpened(props: {
  projectId: string;
  canRestore: boolean;
  hasCollaborators: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.PROJECT_DELETE_DIALOG_OPENED, props);
}

/**
 * Fired when a founder opens the currently-selected project straight from the
 * global Orbit (People DB) view via the open-project button (LEV-889).
 */
export function trackOrbitOpenProjectClicked(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.ORBIT_OPEN_PROJECT_CLICKED, props);
}

/**
 * Fired when a founder opens the LinkedIn import guide modal from the Orbit add
 * menu (LEV-925). The `scope` discriminant ("global" | "project") keeps the two
 * entry points independently measurable; `alreadyImported` reflects whether the
 * guide was the priority-first item or the de-emphasized last item at open time.
 */
export function trackOrbitLinkedInImportGuideOpened(props: {
  scope: "global" | "project";
  alreadyImported: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.ORBIT_LINKEDIN_IMPORT_GUIDE_OPENED, props);
}

/**
 * Fired when a founder opens the Google Contacts import guide modal from the
 * Orbit Import menu (LEV-1044). Mirrors the LinkedIn guide event: `scope`
 * ("global" | "project") keeps the two entry points independently measurable;
 * `alreadyImported` reflects whether Google Contacts had a prior completed
 * import at open time.
 */
export function trackOrbitGoogleImportGuideOpened(props: {
  scope: "global" | "project";
  alreadyImported: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.ORBIT_GOOGLE_IMPORT_GUIDE_OPENED, props);
}

/**
 * LEV-1019: founder changed the sort on an Orbit / pipeline list view. Props are
 * enums/literals only (no PII): `surface` names the list, `field`/`direction`
 * are the chosen sort.
 */
export function trackOrbitListSortChanged(props: {
  surface: "people" | "companies" | "pipeline";
  field: string;
  direction: "asc" | "desc";
}) {
  trackEvent(ANALYTICS_EVENTS.ORBIT_LIST_SORT_CHANGED, props);
}

/**
 * LEV-1019: founder applied or cleared a typed/object filter. `value` is the
 * chosen enum/key or null when cleared — never free text.
 */
export function trackOrbitListFilterChanged(props: {
  surface: "people" | "companies" | "pipeline";
  filterKey: "source" | "company" | "stage";
  value: string | null;
}) {
  trackEvent(ANALYTICS_EVENTS.ORBIT_LIST_FILTER_CHANGED, props);
}

/**
 * LEV-1019: founder toggled a column's visibility (show/hide attribute). Props
 * are the column id + the resulting boolean — no row data.
 */
export function trackOrbitListColumnToggled(props: {
  surface: "people" | "companies" | "pipeline";
  columnId: string;
  visible: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.ORBIT_LIST_COLUMN_TOGGLED, props);
}

/**
 * LEV-1080: founder reordered the properties (columns/attributes) of an Orbit /
 * pipeline list view via drag. Props are the moved column id + its new index —
 * ids and counts only, no row data.
 */
export function trackOrbitListColumnReordered(props: {
  surface: "people" | "companies" | "pipeline";
  columnId: string;
  toIndex: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ORBIT_LIST_COLUMN_REORDERED, props);
}

/**
 * Founder dwelt on (or focused) a pipeline toolbar control long enough to
 * see its hint tooltip (LEV-1093 hints A/B). `control` identifies which
 * toolbar button, never the hint's copy: the copy lives only in
 * `pipelineToolbarHints` so it can change without touching this event.
 */
export function trackPipelineToolbarHintViewed(props: {
  projectId: string;
  pipelineKind: PipelineKind;
  control: ToolbarHintControl;
}) {
  trackEvent(ANALYTICS_EVENTS.PIPELINE_TOOLBAR_HINT_VIEWED, props);
}

/** A funnel-shaping action taken on a pipeline board, for PIPELINE_BOARD_ACTION. */
export type PipelineBoardAction =
  | "find"
  | "add_orbit"
  | "add_manual"
  | "add_csv"
  | "add_apollo"
  | "card_moved"
  | "card_opened"
  | "view_changed"
  | "stage_settings_opened";

/**
 * Founder started the guided pipeline tour, auto (first board visit for that
 * kind) or manual ("Take the tour" button). `stopCount` is the tour's total
 * stop count at start time, for completion-rate denominators.
 */
export function trackPipelineTourStarted(props: {
  projectId: string;
  pipelineKind: PipelineKind;
  trigger: TourStartTrigger;
  stopCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.PIPELINE_TOUR_STARTED, props);
}

/**
 * Founder's tour reached a given stop. `stop` identifies the stop, never its
 * copy; `stepIndex` is 0-based position, `stopCount` the tour's total.
 */
export function trackPipelineTourStepViewed(props: {
  projectId: string;
  pipelineKind: PipelineKind;
  stop: PipelineTourStopId;
  stepIndex: number;
  stopCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.PIPELINE_TOUR_STEP_VIEWED, props);
}

/** Founder walked the tour through its final stop. */
export function trackPipelineTourCompleted(props: {
  projectId: string;
  pipelineKind: PipelineKind;
}) {
  trackEvent(ANALYTICS_EVENTS.PIPELINE_TOUR_COMPLETED, props);
}

/**
 * Founder left the tour before its last stop. `stop`/`stepIndex` are where
 * they were when it ended; `reason` distinguishes an explicit Skip from a
 * dismissal or a target the DOM no longer has (e.g. a control removed by
 * another flag).
 */
export function trackPipelineTourSkipped(props: {
  projectId: string;
  pipelineKind: PipelineKind;
  stop: PipelineTourStopId;
  stepIndex: number;
  reason: TourSkipReason;
}) {
  trackEvent(ANALYTICS_EVENTS.PIPELINE_TOUR_SKIPPED, props);
}

/**
 * Founder started the Overall Evaluation tour, auto (first panel open in this
 * browser) or manual ("Take the tour"). `stopCount` is the tour's total stops.
 */
export function trackEvaluationTourStarted(props: {
  projectId: string;
  trigger: TourStartTrigger;
  stopCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_TOUR_STARTED, props);
}

/** Evaluation tour reached a stop. `stop` is the stop id, never its copy. */
export function trackEvaluationTourStepViewed(props: {
  projectId: string;
  stop: EvaluationTourStopId;
  stepIndex: number;
  stopCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_TOUR_STEP_VIEWED, props);
}

/** Founder walked the evaluation tour through its final stop. */
export function trackEvaluationTourCompleted(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_TOUR_COMPLETED, props);
}

/** Founder left the evaluation tour before its last stop, and how. */
export function trackEvaluationTourSkipped(props: {
  projectId: string;
  stop: EvaluationTourStopId;
  stepIndex: number;
  reason: TourSkipReason;
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_TOUR_SKIPPED, props);
}

/** Founder opened a Lev Learn Term from an evaluation tour stop. */
export function trackEvaluationTourLearnClicked(props: { projectId: string; slug: string }) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_TOUR_LEARN_CLICKED, props);
}

/** Founder opened a pipeline board (Customers, Applicants, or Investors). */
export function trackPipelineBoardViewed(props: {
  projectId: string;
  pipelineKind: PipelineKind;
}) {
  trackEvent(ANALYTICS_EVENTS.PIPELINE_BOARD_VIEWED, props);
}

/**
 * Founder took a funnel-shaping action on a pipeline board. `action` is a
 * closed union of control identifiers, never a row id, name, or free text.
 */
export function trackPipelineBoardAction(props: {
  projectId: string;
  pipelineKind: PipelineKind;
  action: PipelineBoardAction;
}) {
  trackEvent(ANALYTICS_EVENTS.PIPELINE_BOARD_ACTION, props);
}

/**
 * Which toast told the founder that cards arrived on a pipeline board:
 *   - "board_toast": "N new prospects added" for cards the board shows.
 *   - "hidden_add": an add the founder just made that the board is not showing.
 *   - "import_finished": a CSV import finished while the view was narrowed.
 */
export type PipelineEntryAnnouncement = "board_toast" | "hidden_add" | "import_finished";

/**
 * Fired once per toast actually shown. `count` is the number the toast showed;
 * the import-finished line claims no count, so it carries none.
 */
export function trackPipelineEntryAnnounced(
  props:
    | {
        projectId: string;
        pipelineKind: PipelineKind;
        announcement: Exclude<PipelineEntryAnnouncement, "import_finished">;
        count: number;
      }
    | {
        projectId: string;
        pipelineKind: PipelineKind;
        announcement: "import_finished";
      },
) {
  trackEvent(ANALYTICS_EVENTS.PIPELINE_ENTRY_ANNOUNCED, props);
}

/**
 * LEV-1207: an Orbit ICP prospecting scan finished and produced its candidate
 * list. Counts and scores only — never a name, title, company, or location.
 *
 * `peopleScanned` vs `candidateCount` is the calibration signal: the ICP match
 * threshold silently drops everyone below it, so a large scan yielding zero
 * candidates is an empty drawer the founder can't act on, and is
 * indistinguishable from "your Orbit is empty" without this event.
 */
export function trackOrbitProspectScanned(
  props:
    | {
        projectId: string;
        pipeline: string;
        /** The scan failed: no stats, so it never reads as an empty scan. */
        outcome: Exclude<OrbitProspectScanOutcome, "ok">;
      }
    | {
  projectId: string;
  pipeline: string;
  outcome: "ok";
  /** People examined in the founder's Orbit this run. */
  peopleScanned: number;
  /** Candidates that cleared the threshold and were surfaced for review. */
  candidateCount: number;
  /** Score spread of the surfaced candidates (null when none cleared). */
  topScore: number | null;
  medianScore: number | null;
  /** False when the ICP stated no usable signal, so everyone matched. */
  hasIcpSignal: boolean;
},
) {
  // `executionPath` mirrors `captureOrbitProspectScanned` (posthog.ts) so the
  // review-drawer and Inngest-job paths share one event but stay separable.
  trackEvent(ANALYTICS_EVENTS.ORBIT_PROSPECT_SCANNED, {
    ...props,
    executionPath: "review_drawer" as const,
  });
}

/**
 * LEV-1207: the founder confirmed their prospecting review. Comparing
 * `avgSelectedScore` against `avgRejectedScore` tells us whether the scorer
 * ranks the way the founder actually judges — a high-scoring candidate the
 * founder rejects is a scoring bug we'd otherwise never see.
 */
export function trackOrbitProspectConfirmed(props: {
  projectId: string;
  pipeline: string;
  selectedCount: number;
  rejectedCount: number;
  /** Rejections where the founder bothered to type a reason. */
  rejectedWithReasonCount: number;
  avgSelectedScore: number | null;
  avgRejectedScore: number | null;
}) {
  // Carries the same `executionPath` discriminant as `orbit_prospect_scanned`
  // so scanned → confirmed funnels segment consistently. Only the review
  // drawer can produce a confirm today (the Inngest job auto-creates with no
  // review step), but stamping it keeps the two events joinable on one
  // property instead of one of them silently lacking it.
  trackEvent(ANALYTICS_EVENTS.ORBIT_PROSPECT_CONFIRMED, {
    ...props,
    executionPath: "review_drawer" as const,
  });
}

/**
 * Fired when a founder clicks a people-import ("Lev added N people for your
 * Orbit") notification (LEV-1070). The `mode` discriminant keeps the two
 * context-dependent open paths independently measurable: "inline" = already
 * inside a project, so we open that project's Orbit lightbox in place with no
 * navigation; "navigate" = clicked from the home page, so we soft-route to the
 * Orbit People import-history page. `projectId` is set only for the inline path.
 */
export function trackOrbitNotificationClicked(props: {
  mode: "inline" | "navigate";
  projectId?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.ORBIT_NOTIFICATION_CLICKED, props);
}

// ---------------------------------------------------------------------------
// Evaluation / spotlight / feedback / labs / deliverable-regen (§3c)
// ---------------------------------------------------------------------------

export function trackEvaluationInitiated(props: {
  projectId: string;
  rubricType: string;
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_INITIATED, props);
}

/**
 * `journeyState` is the founder evaluation journey's state at open (null with
 * `eval-first-read-journey` off). With the journey on, `rating` is null unless
 * the state is `full`: Locked and First read show no rating, so reporting the
 * stored one would count a grade nobody saw.
 */
export function trackEvaluationViewed(props: {
  projectId: string;
  rubricType?: string | null;
  rating?: string | number | null;
  journeyState: EvaluationJourneyState | null;
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_VIEWED, props);
}

/**
 * One per founder/Lev "+" adoption of an eval-origin task — a legacy flat
 * suggestion OR a task-eval-sync checkpoint step. `checkpointPosition`,
 * `checkpointTotal`, and `actionType` MUST be passed only for a checkpoint
 * step (never a flat improvement), so the properties stay absent — not merely
 * null — on every event this replaces. `actionType` always goes through
 * `normalizeTaskActionTypeForEvent` (ids/enums only, never the raw DB string).
 */
export function trackEvaluationImprovementAddedAsTask(props: {
  projectId: string;
  actionId: string;
  weekNumber?: number | null;
  /** Who does the work (narrative schema `doer`); omitted/null for an
   *  improvement materialized before `doer` existed or with the flag off. */
  doer?: "lev" | "founder" | null;
  /** task-eval-sync: this checkpoint's 1-based position within its eval Quest. */
  checkpointPosition?: number | null;
  /** task-eval-sync: the Quest's total checkpoint count, paired with `checkpointPosition`. */
  checkpointTotal?: number | null;
  /** task-eval-sync: the checkpoint's actionType, normalized below. */
  actionType?: string | null;
  /** The surface whose "+" made the add; absent on the legacy improvement add. */
  addedFrom?: "eval_page" | "sidebar_suggestion" | "kanban_board";
  /** The shared task-engagement surface; `addedFrom` stays for existing dashboards. */
  surface: TaskSurface;
}) {
  const { checkpointPosition, checkpointTotal, actionType, addedFrom, surface, ...base } =
    props;
  trackEvent(ANALYTICS_EVENTS.EVALUATION_IMPROVEMENT_ADDED_AS_TASK, {
    ...base,
    surface: normalizeTaskSurface(surface),
    ...(addedFrom != null ? { addedFrom } : {}),
    ...(checkpointPosition != null ? { checkpointPosition } : {}),
    ...(checkpointTotal != null ? { checkpointTotal } : {}),
    ...(actionType != null
      ? { actionType: normalizeTaskActionTypeForEvent(actionType) }
      : {}),
  });
}

/** One per preview request; MUST never carry the generated text. */
export function trackEvaluationFirstReadPreviewed(props: {
  projectId: string;
  rubricType: string | null;
  outcome: "shown" | "failed";
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_FIRST_READ_PREVIEWED, props);
}

/**
 * The First read's next-step button, its only action. MUST never carry the
 * model's text.
 */
export function trackEvaluationFirstReadActionClicked(props: {
  projectId: string;
  stepId: string;
  surface: "preview" | "journey";
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_FIRST_READ_ACTION_CLICKED, props);
}

/** The Locked card asked Lev to write the gate blocks it lists in `missingSteps`. */
export function trackEvaluationFirstReadRequested(props: {
  projectId: string;
  rubricType: string | null;
  missingSteps: ("tam" | "competitive")[];
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_FIRST_READ_REQUESTED, props);
}

/**
 * One per Brutal read request. `from` is the view the founder opened it from.
 * MUST never carry the generated text.
 */
export function trackEvaluationBrutalReadViewed(props: {
  projectId: string;
  rubricType: string | null;
  outcome: "shown" | "failed";
  from: "first_read" | "full";
}) {
  trackEvent(ANALYTICS_EVENTS.EVALUATION_BRUTAL_READ_VIEWED, props);
}

/**
 * A founder opened an inline concept annotation. `surface` distinguishes where
 * the annotation lived, so we can tell whether canvas annotation earns its
 * place independently of the Labs browser.
 */
export function trackLearnConceptOpened(props: {
  conceptSlug: string;
  domain: string;
  surface: "canvas" | "document" | "labs";
  projectId?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_CONCEPT_OPENED, props);
}

/**
 * "I know this" — the founder is telling us the annotation was unwanted. In
 * aggregate a high rate means the corpus is pitched too basic; per-founder it
 * becomes profile evidence once the profile exists.
 */
export function trackLearnConceptKnown(props: {
  conceptSlug: string;
  domain: string;
  surface: "canvas" | "document" | "labs";
  projectId?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_CONCEPT_KNOWN_CLICKED, props);
}

/** "Go deeper" — the opposite signal, and a click through to the full page. */
export function trackLearnConceptDeeper(props: {
  conceptSlug: string;
  domain: string;
  surface: "canvas" | "document" | "labs";
  projectId?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_CONCEPT_DEEPER_CLICKED, props);
}

/**
 * A visitor searched the concept library. Carries no text — just whether the
 * search found anything, which is all that is needed to measure the library's
 * hit rate.
 */
export function trackLearnTermSearched(props: {
  resultCount: number;
  queryLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_TERM_SEARCHED, props);
}

/**
 * A visitor changed a filter on the System Card release map. `value` is one of
 * the map's fixed option keys (or a modality from the release data), never
 * visitor text.
 */
export function trackLearnReleaseMapFilter(props: {
  filter: "weights" | "type" | "range";
  value: string;
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_RELEASE_MAP_FILTER, props);
}

/**
 * A visitor looked for a concept the library does not have, and the term passed
 * screening. This is the demand signal the content backlog is built from.
 *
 * ## Why this one carries text, when the analytics rules forbid it
 *
 * `CLAUDE.md` is explicit: analytics carry counts, booleans, lengths, domains,
 * ids, or non-reversible hashes — never raw user text. That rule exists because
 * free-text fields silently accumulate PII and secrets, and a hash would satisfy
 * it here. A hash would also make the event worthless: the entire purpose is a
 * human reading a ranked list of the words founders typed.
 *
 * So the term is carried, and what reduces the risk is that it is not raw. Only
 * a `candidate` verdict from `screenTermProposal` reaches this function, and a
 * candidate has already been normalised, capped at 60 characters and six words,
 * restricted to an allowlisted character set that excludes markup and URLs, and
 * required to overlap the vocabulary of concepts we already publish.
 *
 * ## What that screen does NOT guarantee
 *
 * An earlier version of this docblock claimed a string "cannot be an email
 * address, a credential, or a sentence about someone's health and also score as
 * a startup term." That is too strong, and the overstatement is the dangerous
 * part, because it invites callers to treat the screen as de-identification.
 * It is not: it is an abuse and injection filter that happens to correlate with
 * de-identification. `MIN_DOMAIN_RELEVANCE` is 0.5, so a phrase passes when half
 * its meaningful words are on-domain — and "john smith cap table" scores exactly
 * 0.5 on `cap` and `table`. A person's name rides through alongside two domain
 * words. An email address genuinely cannot pass, but only because `@` fails the
 * character allowlist, which is a narrower property than the old claim implied.
 *
 * The honest description: this is raw user text with a domain filter in front of
 * it, accepted deliberately because a hash makes the backlog unreadable and the
 * backlog is the entire point. The real fix is to route the term to a table and
 * keep only counts and scores in analytics — scoped in
 * `docs/plans/2026-08-10-lev-learn-credit-and-monthly-digest.md`, which already
 * concludes that storage, not an analytics property, is where a term belongs.
 * Raised in PR #578 review; deferred there rather than silently accepted.
 *
 * Callers MUST pass a screened term. Passing raw input would defeat every one
 * of those properties in one line.
 */
export function trackLearnTermMissed(props: {
  /** The normalised term from a `candidate` verdict — never raw input. */
  screenedTerm: string;
  /** Domain-relevance score, so the backlog can be ranked. */
  score: number;
  surface: LearnTermMissSurface;
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_TERM_MISSED, props);
}

// `learn_term_requested` (the deliberate cousin of the ambient miss above) is
// emitted SERVER-side by the term-request route — the endpoint is a mutation
// callable outside the UI, so the event lives at that boundary. See
// `captureLearnTermRequested` in `src/lib/posthog.ts` (PR #596 review).
//
// The pair living on different layers is intentional, not drift: a miss is an
// ambient browser-only signal with no server mutation to anchor to, while a
// request has exactly one reliable boundary — the POST. The person-identity
// join between them survives the split because the request POST carries the
// browser's own PostHog distinct id, so both events land on the same person.

/**
 * A reader sorted a System Card table. `direction` "original" is the third
 * click that restores the edition's authored order.
 */
export function trackLearnSystemCardSort(props: {
  slug: string;
  /** The column header as authored in the edition constant. */
  column: string;
  direction: "asc" | "desc" | "original";
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_SYSTEM_CARD_SORT, props);
}

export type LearnSystemCardShareChannel = "linkedin" | "copy_post" | "copy_email" | "email" | "copy_link";

/** A reader used one System Card share action. Never carries the shared text, which a reader may edit. */
export function trackLearnSystemCardShare(props: {
  slug: string;
  section: string;
  channel: LearnSystemCardShareChannel;
}) {
  trackEvent(ANALYTICS_EVENTS.LEARN_SYSTEM_CARD_SHARE, props);
}

/** A reader clicked "Download the skill" on a System Card edition. Counts clicks; direct fetches of the file are not seen. */
export function trackLearnSystemCardSkillDownloaded(props: { slug: string }) {
  trackEvent(ANALYTICS_EVENTS.LEARN_SYSTEM_CARD_SKILL_DOWNLOADED, props);
}

export function trackLabsFeatureViewed(props: {
  projectId: string;
  featureSlug: string;
}) {
  trackEvent(ANALYTICS_EVENTS.LABS_FEATURE_VIEWED, props);
}

export function trackInvestorResearchCompetitorSectionToggled(props: {
  projectId: string;
  isOpen: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_RESEARCH_COMPETITOR_SECTION_TOGGLED, props);
}

/** `source` distinguishes the Top 5 action-list jumps from the warm-banner jumps. */
export function trackInvestorResearchProfileJumpClicked(props: {
  projectId: string;
  source: "top_five" | "warm_banner";
  firmKey: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_RESEARCH_PROFILE_JUMP_CLICKED, props);
}

/**
 * A partner's researched link (blog, podcast, profile) opened from a partner
 * row. `linkKind` and `position` only: the label and URL are model-written
 * and can name a person.
 */
export function trackInvestorResearchPartnerLinkClicked(props: {
  projectId: string;
  linkKind: "social" | "writing" | "audio_video" | "other";
  position: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_RESEARCH_PARTNER_LINK_CLICKED, props);
}

/** Never the address or the contact's name: both identify a real person. */
export function trackInvestorResearchWarmIntroEmailClicked(props: {
  projectId: string;
  /** The partner's own row, or the firm line when no partner is named. */
  surface: "partner_row" | "firm_fallback";
  /** The founder knows the partner, or knows someone who can introduce them. */
  relationship: "direct" | "via";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_RESEARCH_WARM_INTRO_EMAIL_CLICKED, props);
}

/**
 * A Venture Graph firm or partner page opened from the target list. `firmKey`
 * is the normalized firm name `investor_research_profile_jump_clicked` already
 * sends; never the partner's name.
 */
export function trackInvestorResearchVentureGraphLinkClicked(props: {
  projectId: string;
  firmKey: string;
  target: "firm" | "partner";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_RESEARCH_VENTURE_GRAPH_LINK_CLICKED, props);
}

export function trackInvestorResearchGenerateClicked(props: {
  projectId: string;
  section: InvestorResearchStage;
  isRegenerate: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_RESEARCH_GENERATE_CLICKED, props);
}

export function trackMaAdvisorGenerateClicked(props: {
  projectId: string;
  section: MaAdvisorStage;
  isRegenerate: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.MA_ADVISOR_GENERATE_CLICKED, props);
}

type LevdevBuilderTarget = "lovable" | "claude" | "aws" | "google";

export function trackLevdevBuilderSelected(props: {
  projectId: string;
  builder: LevdevBuilderTarget;
  score: number;
  verdict: "high" | "medium" | "low";
  wasRecommended: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LEVDEV_BUILDER_SELECTED, props);
}

export function trackLevdevLaunched(props: {
  projectId: string;
  target: LevdevBuilderTarget;
  score: number;
  descopedCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LEVDEV_LAUNCHED, props);
}

export function trackLiftoffStoryGenerateClicked(props: {
  projectId: string;
  isRegenerate: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_STORY_GENERATE_CLICKED, props);
}

export function trackLiftoffStoryGenerated(props: {
  projectId: string;
  slideCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_STORY_GENERATED, props);
}

export function trackLiftoffStoryGenerationFailed(props: { projectId: string }) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_STORY_GENERATION_FAILED, props);
}

export function trackLiftoffEvaluateClicked(props: {
  projectId: string;
  brutal: boolean;
  isReeval: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_EVALUATE_CLICKED, props);
}

export function trackLiftoffDeckEvaluated(props: {
  projectId: string;
  brutal: boolean;
  overall: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_DECK_EVALUATED, props);
}

export function trackLiftoffAnswersApplied(props: {
  projectId: string;
  answerCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_ANSWERS_APPLIED, props);
}

export function trackLiftoffVoiceInputUsed(props: {
  projectId: string;
  surface: "briefing" | "sharpen";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_VOICE_INPUT_USED, props);
}

export function trackLiftoffExportCopied(props: {
  projectId: string;
  format: "markdown";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_EXPORT_COPIED, props);
}

export function trackLiftoffExportDownloaded(props: {
  projectId: string;
  format: "markdown" | "pptx" | "pdf";
  /** How many slides carried a grounded art-directed chart (counts only). */
  artDirectedSlides?: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_EXPORT_DOWNLOADED, props);
}

export function trackLiftoffBuilderPromptCopied(props: {
  projectId: string;
  target: "lovable" | "figma-make" | "ai-studio" | "google-slides";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_BUILDER_PROMPT_COPIED, props);
}

export function trackInvestorQuestionsGenerated(props: {
  projectId: string;
  /** Total questions in the blended set (counts only — no question text). */
  questionCount: number;
  /** How many were tailored by AI vs the canonical bank. */
  aiCount: number;
  isRegenerate: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_QUESTIONS_GENERATED, props);
}

export function trackInvestorQuestionsAnswerCoached(props: {
  projectId: string;
  confidence: "strong" | "needs_work" | "gap";
  /** Length of the founder's draft in characters — a size signal, not content. */
  draftLength: number;
  gapCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_QUESTIONS_ANSWER_COACHED, props);
}

export function trackInvestorQuestionsAnswerSaved(props: {
  projectId: string;
  /** Whether the founder adopted the coach's suggested answer or wrote their own. */
  source: "coach" | "own";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_QUESTIONS_ANSWER_SAVED, props);
}

/**
 * The Fundraising row.
 *
 * The row is a funnel and these are its steps, so the properties are the ones
 * that answer "where do founders stop": which path they picked, whether they
 * brought a deck or built one, what it scored, and whether the gaps turned
 * into work. Counts, enums and lengths only — never deck text, slide content,
 * or an investor's name.
 */
export function trackInvestorStudioFundingPathChosen(props: {
  projectId: string;
  path: string;
  /** Whether Lev fully serves this path — the caveat's real-world hit rate. */
  isVenture: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_FUNDING_PATH_CHOSEN, props);
}

/**
 * The founder moved between blocks without going back to the canvas.
 *
 * No free text: block ids are a closed set, never a founder's own words.
 * `to` is always a real block id (BlockNav only ever navigates to one);
 * `from` adds "unknown" because the founder's current position can be
 * off the rail's own list on the very first block, where there is no
 * previous one to name (Copilot review, PR #719 — this was `string`,
 * which let a typo or an unnormalized value slip into analytics silently).
 */
export function trackInvestorStudioBlockNavUsed(props: {
  projectId: string;
  from: StudioJobId | "unknown";
  to: StudioJobId;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_BLOCK_NAV_USED, props);
}

/**
 * The founder clicked the Fundraising wizard's intro-panel CTA to start the
 * path (opens the first deliverable, studio-path). Ids only — no free text.
 */
export function trackFundraisingWizardPrimaryCtaClicked(props: {
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.FUNDRAISING_WIZARD_PRIMARY_CTA_CLICKED, props);
}

/**
 * The founder advanced from one Fundraising deliverable to the next via the
 * wizard's forward CTA. Both ids are a closed set (StudioJobId).
 */
export function trackFundraisingWizardDeliverableAdvanced(props: {
  projectId: string;
  from_deliverable_id: StudioJobId;
  to_deliverable_id: StudioJobId;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.FUNDRAISING_WIZARD_DELIVERABLE_ADVANCED, props);
}

/**
 * The founder revealed the LevAI chat on a Fundraising block, which starts
 * hidden there (the row is a linear wizard). Counts only.
 */
export function trackFundraisingWizardChatRevealed(props: {
  projectId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.FUNDRAISING_WIZARD_CHAT_REVEALED, props);
}

export function trackInvestorStudioDeckModeChosen(props: {
  projectId: string;
  /** The question this whole screen was redesigned around. */
  mode: "upload" | "build";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_DECK_MODE_CHOSEN, props);
}

export function trackInvestorStudioPromotedDeckAction(props: {
  projectId: string;
  /** The FR4 card (LEV-1420): score the promoted deck, or replace the file. */
  action: "score" | "replace";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_PROMOTED_DECK_ACTION, props);
}

export function trackInvestorStudioDeckUploaded(props: {
  projectId: string;
  format: "pdf" | "pptx";
  /** Size in bytes — a shape signal for the parser, not content. */
  fileBytes: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_DECK_UPLOADED, props);
}

export function trackInvestorStudioDeckUploadFailed(props: {
  projectId: string;
  /** Where it broke, so parse failures are separable from upload failures. */
  stage: "upload" | "extract" | "score";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_DECK_UPLOAD_FAILED, props);
}

export function trackInvestorStudioDeckScored(props: {
  projectId: string;
  /** Rounded to one decimal — the rubric's 1-5 overall. */
  overall: number;
  objectionCount: number;
  highSeverityCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_DECK_SCORED, props);
}

/**
 * The founder took their deck out to a design tool.
 *
 * `target` is the signal worth having: it says which tool founders actually
 * reach for, which is what tells us whether the handoff prompt should stay
 * generic or get tuned per tool.
 */
export function trackInvestorStudioDeckHandoffCopied(props: {
  projectId: string;
  /*
    The literal union, not `string`. The source has a canonical HandoffTarget,
    and widening it here lets a future call site send anything — which
    fragments the property silently, and analytics only reveals that months
    later when someone asks which tool founders actually pick.
  */
  target: HandoffTarget;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_DECK_HANDOFF_COPIED, props);
}

export function trackInvestorStudioDeckVersionGenerated(props: {
  projectId: string;
  variant: "full" | "short" | "updated";
  groundedInAnswers: boolean;
  /** False when the render failed — the version exists but has no file. */
  exportAvailable: boolean;
}) {
  trackEvent(
    ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_DECK_VERSION_GENERATED,
    props,
  );
}

export function trackInvestorStudioDeckOutlineCopied(props: {
  projectId: string;
}) {
  // The signal for "founders design elsewhere": if this outruns .pptx
  // downloads, the handoff matters more than the built-in rendering.
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_DECK_OUTLINE_COPIED, props);
}

export function trackInvestorStudioGapsSentToQuestions(props: {
  projectId: string;
  /** How many NEW questions landed; 0 means they were already there. */
  added: number;
}) {
  trackEvent(
    ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_GAPS_SENT_TO_QUESTIONS,
    props,
  );
}

export function trackInvestorStudioFaqCopied(props: {
  projectId: string;
  answeredCount: number;
  /*
    Which copy the founder used: the whole FAQ, or one answer.

    They are different intentions. "document" is preparing to send something
    ahead of a meeting; "answer" is replying to an investor who just asked one
    question, which is the reason the per-answer control exists at all.
    Optional so existing call sites keep reporting.
  */
  scope?: "document" | "answer";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_STUDIO_FAQ_COPIED, props);
}

export function trackInvestorQuestionsDocExported(props: {
  projectId: string;
  format: "markdown";
  answeredCount: number;
  totalCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.INVESTOR_QUESTIONS_DOC_EXPORTED, props);
}

export function trackLiftoffDeckThemeChanged(props: {
  projectId: string;
  theme: DeckThemeId;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_DECK_THEME_CHANGED, props);
}

export function trackLiftoffDeckStyleChanged(props: {
  projectId: string;
  style: LiftoffSlideStyle;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_DECK_STYLE_CHANGED, props);
}

export function trackLiftoffSlidePhotoApplied(props: {
  projectId: string;
  provider: "pexels";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.LIFTOFF_SLIDE_PHOTO_APPLIED, props);
}

export function trackCraftyGtmGenerateClicked(props: {
  projectId: string;
  mode: CraftyGtmMode;
  isRegenerate: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.CRAFTY_GTM_GENERATE_CLICKED, props);
}

export function trackAcceleratorMatchRunClicked(props: {
  projectId: string;
  stage: string;
  regionCount: number;
  includeStudios: boolean;
  isRerun: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_MATCH_RUN_CLICKED, props);
}

export function trackAcceleratorMatchDraftAppClicked(props: {
  projectId: string;
  programId: string;
  appFormatId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_MATCH_DRAFT_APP_CLICKED, props);
}

// AI Moat (Labs). Page views go through the shared trackLabsFeatureViewed
// above; only this feature-specific click is its own event. Generation outcomes
// are captured server-side (see src/lib/posthog.ts), where the derived quadrant
// and the per-class counts actually exist.

export function trackAiMoatMapGenerateClicked(props: {
  projectId: string;
  /** Joins the click to the two server-side mapped events it produces. */
  mapRunId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.AI_MOAT_MAP_GENERATE_CLICKED, props);
}

export function trackFeedbackButtonClicked(props?: { location?: string }) {
  trackEvent(ANALYTICS_EVENTS.FEEDBACK_BUTTON_CLICKED, props ?? {});
}

export function trackDeliverableManualGenerateClicked(props: {
  projectId: string;
  deliverableId: string;
  parentDocumentId: string;
  deliverableType?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.DELIVERABLE_MANUAL_GENERATE_CLICKED, props);
}

export function trackDeliverableRegenerated(props: {
  projectId: string;
  deliverableId: string;
  parentDocumentId: string;
  source: "user_click" | "levai";
}) {
  trackEvent(ANALYTICS_EVENTS.DELIVERABLE_REGENERATED, props);
}

// SWW intro video tracking
export function trackSwwVideoOpened(props: { page: string }) {
  trackEvent(ANALYTICS_EVENTS.SWW_VIDEO_OPENED, props);
}

export function trackSwwVideoPlayed(props: {
  page: string;
  currentTime: number;
}) {
  trackEvent(ANALYTICS_EVENTS.SWW_VIDEO_PLAYED, props);
}

export function trackSwwVideoCompleted(props: {
  page: string;
  duration: number;
}) {
  trackEvent(ANALYTICS_EVENTS.SWW_VIDEO_COMPLETED, props);
}

// ---------------------------------------------------------------------------
// Accelerator Apps
// ---------------------------------------------------------------------------

export function trackAccelAppGenerateClicked(props: {
  projectId: string;
  formatId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_GENERATE_CLICKED, props);
}

export function trackAccelAppAutoStarted(props: {
  projectId: string;
  formatId: string;
  startedBy: "canvas_held_draft" | "application_view" | "program_handoff";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_AUTO_STARTED, props);
}

export function trackAccelAppBrutalModeRun(props: {
  projectId: string;
  formatId: string;
  brutalScore: number;
  normalScore: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_BRUTAL_MODE_RUN, props);
}

export function trackAccelAppBrutalVerdictRequested(props: { projectId: string; formatId: string }) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_BRUTAL_VERDICT_REQUESTED, props);
}

export function trackAccelAppInterviewQuestionsDraftRequested(props: { projectId: string; formatId: string }) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_INTERVIEW_QUESTIONS_DRAFT_REQUESTED, props);
}

export function trackAccelAppCancelClicked(props: { projectId: string; formatId: string }) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_CANCEL_CLICKED, props);
}

/** What a score was for: a first draft (or a redraft), or a Recalculate. */
export type AccelAppScoreCalculationType = "initial" | "recalculate";

/** A score the founder's draft or Recalculate asked for landed. Carries counts only. */
export function trackAccelAppScoreCalculated(props: {
  projectId: string;
  formatId: string;
  calculationType: AccelAppScoreCalculationType;
  overallScore: number;
  suggestionsCount: number;
  durationMs: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_SCORE_CALCULATED, props);
}

/**
 * The founder clicked Recalculate. `answerCount` is how many answers it sends;
 * `rescoreOnly` is a rescore of an edited draft with none. Counts only, never text.
 */
export function trackAccelAppRecalculateClicked(props: {
  projectId: string;
  formatId: string;
  answerCount: number;
  rescoreOnly: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_RECALCULATE_CLICKED, props);
}

export function trackAccelAppRegenerated(props: {
  projectId: string;
  formatId: string;
  answerCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_REGENERATED, props);
}

export function trackAccelAppInterviewQuestionAnswered(props: {
  projectId: string;
  formatId: string;
  questionId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_INTERVIEW_QUESTION_ANSWERED, props);
}

export function trackAccelAppAnswerCopied(props: {
  projectId: string;
  formatId: string;
  questionId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_ANSWER_COPIED, props);
}

export function trackAccelAppAnswerSaved(props: {
  projectId: string;
  formatId: string;
  questionId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_ANSWER_SAVED, props);
}

export function trackAccelAppAnswerCoached(props: {
  projectId: string;
  formatId: string;
  questionId: string;
  stage: CoachStage;
  brutal: boolean;
  confidence: "strong" | "needs_work" | "gap";
  answerLength: number;
  missingCount: number;
  recoach: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_ANSWER_COACHED, props);
}

export function trackAccelAppCoachSuggestionUsed(props: {
  projectId: string;
  formatId: string;
  questionId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_COACH_SUGGESTION_USED, props);
}

export function trackAccelAppCoachedAnswerUsed(props: {
  projectId: string;
  formatId: string;
  questionId: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_COACHED_ANSWER_USED, props);
}

export function trackAccelAppDetailSaved(props: {
  projectId: string;
  formatId: string;
  fieldId: string;
  filled: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_DETAIL_SAVED, props);
}

export function trackAccelAppAnswersCopiedAll(props: {
  projectId: string;
  formatId: string;
  count: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_ANSWERS_COPIED_ALL, props);
}

export function trackAccelAppExported(props: {
  projectId: string;
  formatId: string;
  kind: "markdown";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_EXPORTED, props);
}

export function trackAccelAppSuggestionAnswered(props: {
  projectId: string;
  formatId: string;
  suggestionId: string;
  suggestionSection: string;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_SUGGESTION_ANSWERED, props);
}

export function trackAccelAppOneLinerSelected(props: {
  projectId: string;
  formatId: string;
  oneLinerLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_ONE_LINER_SELECTED, props);
}

export function trackAccelAppVisionScaleChanged(props: {
  projectId: string;
  formatId: string;
  level: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_VISION_SCALE_CHANGED, props);
}

export function trackAccelAppVisionStatementSaved(props: {
  projectId: string;
  formatId: string;
  statementLength: number;
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.ACCEL_APP_VISION_STATEMENT_SAVED, props);
}

// ---------------------------------------------------------------------------
// Labs: Pitch Perfect (client-side)
// ---------------------------------------------------------------------------

/**
 * Fires when the user starts playback of a synthesized pitch audio file.
 * Server-side captures cover generation/synthesis success + failure; this
 * is the play-engagement signal that only the client sees.
 */
export function trackPitchPerfectAudioPlayed(props: {
  projectId: string;
  draftId: string;
  voice: "onyx" | "nova" | "alloy" | "shimmer";
}) {
  trackEvent(ANALYTICS_EVENTS_LABS.PITCH_PERFECT_AUDIO_PLAYED, props);
}

// ---------------------------------------------------------------------------
// LEV-1396: Signup gate analytics (anonymous project visitors)
// ---------------------------------------------------------------------------

/** A gated action was attempted and the signup prompt was shown. */
export function trackSignupGateImpression(props: {
  projectId: string;
  action: string;
  /** "configured" = the tunable gate payload; "required" = hard account boundary. */
  enforcement?: "configured" | "required";
  /** UI entry point that triggered the prompt (e.g. "lightbox", "sidebar", "canvas"). */
  surface?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SIGNUP_GATE_IMPRESSION, props);
}

/** The visitor clicked the signup button on the signup prompt. */
export function trackSignupGateSignupClicked(props: {
  projectId: string;
  action: string;
  enforcement?: "configured" | "required";
  surface?: string;
}) {
  trackEvent(ANALYTICS_EVENTS.SIGNUP_GATE_SIGNUP_CLICKED, props);
}

/** The visitor dismissed the signup prompt without clicking signup. */
export function trackSignupGateDismissed(props: {
  projectId: string;
  action: string;
  enforcement?: "configured" | "required";
  surface?: string;
  /** How they said no: the "Not now" button, Escape, or a backdrop click. */
  method?: "button" | "escape" | "backdrop";
}) {
  trackEvent(ANALYTICS_EVENTS.SIGNUP_GATE_DISMISSED, props);
}

/**
 * The anonymous visitor's canvas finished loading (LEV-1513). Fired once per
 * mount; `arrivalCeremony` is true for the first arrival to this project in
 * this browser — the landing that runs the auto-open + reveal sequence.
 */
export function trackAnonymousCanvasViewed(props: {
  projectId: string;
  arrivalCeremony: boolean;
}) {
  trackEvent(ANALYTICS_EVENTS.ANONYMOUS_CANVAS_VIEWED, props);
}

/**
 * LEV-1594: an anonymous visitor with a live claim cookie saw the resume
 * notice instead of the onboarding chat input. Fired once per mount.
 * landingPageSlug only — no projectId at impression time (matches sibling
 * anon impression events).
 */
export function trackAnonProjectResumeNoticeShown(props: {
  landingPageSlug: LandingPageSlug;
}) {
  trackEvent(ANALYTICS_EVENTS.ANONYMOUS_PROJECT_RESUME_NOTICE_SHOWN, props);
}

/**
 * LEV-1594: an anonymous visitor clicked the "Continue to your project" link
 * on the resume notice. Fired on the click event.
 */
export function trackAnonProjectResumeCanvasClicked(props: {
  landingPageSlug: LandingPageSlug;
}) {
  trackEvent(ANALYTICS_EVENTS.ANONYMOUS_PROJECT_RESUME_CANVAS_CLICKED, props);
}

// ---------------------------------------------------------------------------
// ICP builder (icp-builder spec)
// ---------------------------------------------------------------------------

/**
 * A founder toggled or added a chip on one of the ICP dials. `dial` is the
 * dial id (e.g. "role"), never founder-entered text — the dial VALUES (role
 * names, locations, titles) are exactly what CLAUDE.md's analytics rule
 * forbids in properties. The panel debounces before calling this, so one call
 * is one settled edit, not one per click.
 */
export function trackIcpDialChanged(props: {
  projectId: string;
  dial: IcpEditableDialId;
  clusterCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ICP_DIAL_CHANGED, {
    projectId: props.projectId,
    dial: props.dial,
    clusterCount: props.clusterCount,
  });
}

/**
 * The founder chose to describe their own ICP instead of the listed
 * candidates. Counts only: what they then type is founder text and never
 * leaves the chat.
 */
export function trackIcpDescribeOwnClicked(props: {
  projectId: string;
  candidateCount: number;
}) {
  trackEvent(ANALYTICS_EVENTS.ICP_DESCRIBE_OWN_CLICKED, {
    projectId: props.projectId,
    candidateCount: props.candidateCount,
  });
}

/** First-touch lifecycle marker for the ICP builder's first save. */
export function markFirstIcpSaved() {
  setLifecycleOnce({ first_icp_saved_at: new Date().toISOString() });
}

/** The founder set a Customer Targets card's reach path. Never the "via" text, which names a person. */
export function trackCustomerTargetReachPathSet(props: {
  projectId: string;
  reachPath: "cold" | "warm";
}) {
  trackEvent(ANALYTICS_EVENTS.CUSTOMER_TARGET_REACH_PATH_SET, {
    projectId: props.projectId,
    reachPath: props.reachPath,
  });
}

// ---------------------------------------------------------------------------
// Auth lifecycle events
// ---------------------------------------------------------------------------

/**
 * Sent right after the synchronous posthog.identify call for a just-created
 * account, so the PostHog Slack destination can read the person properties
 * identify carries. Once per browser tab (ClerkIdentify's sessionStorage
 * guard), once per page load when storage is blocked; PostHog's 30-min
 * per-person mask makes repeats harmless. No properties — the destination
 * reads the person record, not the event.
 * fixed on 2026-09-18: LEV-1786
 */
export function trackUserFirstSessionStarted() {
  trackEvent(ANALYTICS_EVENTS.USER_FIRST_SESSION_STARTED);
}
